import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';
import { canMyEventsOpenSAFForm } from '../utils/safAccess';
import {
  getMyEventsUrlParams,
} from '../utils/myEventsUrlParams';
import './MyEvents.css';

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const HammerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9" />
    <path d="m18 15 4-4" />
    <path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756l-1.77 1.77" />
    <path d="m2 2 7.586 7.586" />
  </svg>
);

const OpenInNewTabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);

const ListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="8" x2="21" y1="6" y2="6" />
    <line x1="8" x2="21" y1="12" y2="12" />
    <line x1="8" x2="21" y1="18" y2="18" />
    <line x1="3" x2="3.01" y1="6" y2="6" />
    <line x1="3" x2="3.01" y1="12" y2="12" />
    <line x1="3" x2="3.01" y1="18" y2="18" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const FolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);

const REGISTRATION_CSV_HEADERS = [
  'Event Title',
  'Event Date',
  'First Name',
  'Last Name',
  'Grade',
  'Attended',
  'Hours Verified',
  'Hours',
  'Verification PDF',
  'Registration ID',
];

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const getRegistrationAttendanceCsvValue = (registration) => {
  if (registration.attended === null) return 'Not marked';
  if (registration.attended === false) return 'Absent';
  return registration.attendance ? `Present (${registration.attendance})` : 'Present';
};

const getRegistrationHoursVerifiedCsvValue = (registration) => {
  if (registration.status === 'Verified') return 'Yes';
  if (registration.status === 'Pending') return 'Pending';
  return 'No';
};

const registrationToCsvRow = (registration, event = null) => ({
  'Event Title': event?.title || '',
  'Event Date': event?.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : '',
  'First Name': registration.first_name || '',
  'Last Name': registration.last_name || '',
  Grade: registration.gradelevel || '',
  Attended: getRegistrationAttendanceCsvValue(registration),
  'Hours Verified': getRegistrationHoursVerifiedCsvValue(registration),
  Hours: registration.status === 'Verified' && registration.hours != null ? registration.hours : '',
  'Verification PDF': registration.pdflink || '',
  'Registration ID': registration.id || '',
});

const downloadCsv = (headers, rows, filename) => {
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadRegistrationsCsv = (rows, filename) => {
  downloadCsv(REGISTRATION_CSV_HEADERS, rows, filename);
};

const EVENT_CSV_HEADERS = [
  'Flier Link',
  'Title',
  'Date',
  'Type',
  'Stage',
  'Status',
  'Number of Registrations',
  'Students Attended',
  'Total Hours Received',
  'Average Hours per Student',
];

const computeEventRegistrationStats = (registrations) => {
  const registrationCount = registrations.length;
  const attendedCount = registrations.filter((registration) => registration.attended === true).length;
  const verifiedWithHours = registrations.filter(
    (registration) => registration.status === 'Verified'
      && registration.hours != null
      && !Number.isNaN(Number(registration.hours))
  );
  const totalHours = verifiedWithHours.reduce((sum, registration) => sum + Number(registration.hours), 0);
  const averageHours = verifiedWithHours.length > 0
    ? Math.round((totalHours / verifiedWithHours.length) * 100) / 100
    : 0;

  return {
    registrationCount,
    attendedCount,
    totalHours,
    averageHours,
  };
};

const eventToCsvRow = (event, stats) => ({
  'Flier Link': event.coverPhoto || event.coverphoto || '',
  Title: event.title || '',
  Date: event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : '',
  Type: event.type || '',
  Stage: event.stage || '',
  Status: event.status || '',
  'Number of Registrations': stats.registrationCount ?? 0,
  'Students Attended': stats.attendedCount ?? 0,
  'Total Hours Received': stats.totalHours ?? 0,
  'Average Hours per Student': stats.averageHours ?? 0,
});

const downloadEventsCsv = (rows, filename) => {
  downloadCsv(EVENT_CSV_HEADERS, rows, filename);
};

const slugifyFilename = (value) => (
  (value || 'event').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'event'
);

const getEventFlierUrl = (event) => event.coverPhoto || event.coverphoto || '';

const formatEventDateForFilename = (event) => {
  if (!event?.date) return 'no-date';
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return 'no-date';
  const year = eventDate.getFullYear();
  const month = String(eventDate.getMonth() + 1).padStart(2, '0');
  const day = String(eventDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFileExtensionFromUrl = (url) => {
  if (!url) return '';
  try {
    const pathname = new URL(url).pathname;
    const ext = pathname.split('.').pop();
    if (ext && ext.length <= 5 && !ext.includes('/')) return ext.toLowerCase();
  } catch {
    // ignore invalid URLs
  }
  return '';
};

const buildFlierFilename = (event) => {
  const flierUrl = getEventFlierUrl(event);
  const ext = getFileExtensionFromUrl(flierUrl) || 'jpg';
  return `flier-${formatEventDateForFilename(event)}-${slugifyFilename(event.title)}.${ext}`;
};

const buildVerificationFilename = (event, registration) => {
  const student = slugifyFilename(registration.student_name || 'student');
  return `verification-${formatEventDateForFilename(event)}-${slugifyFilename(event.title)}-${student}.pdf`;
};

const buildEventFolderName = (event) => (
  `${formatEventDateForFilename(event)}-${slugifyFilename(event.title)}`
);

const buildEventDocumentsFolder = (event, { includeFlier = false, registrations = [] } = {}) => {
  const files = [];

  if (includeFlier) {
    const flierUrl = getEventFlierUrl(event);
    if (flierUrl) {
      files.push({ url: flierUrl, filename: buildFlierFilename(event) });
    }
  }

  registrations.forEach((registration) => {
    if (!registrationIsDownloadable(registration)) return;
    files.push({
      url: registration.pdflink,
      filename: buildVerificationFilename(event, registration),
    });
  });

  if (files.length === 0) return null;

  return {
    folderName: buildEventFolderName(event),
    files,
  };
};

const verificationSelectionKey = (eventId, registrationId) => `${eventId}:${registrationId}`;

const getVerificationRowState = (registration) => {
  if (registration.attended === false) return 'absent';
  if (registration.status !== 'Verified' || !registration.pdflink) return 'unverified';
  return 'verified';
};

const registrationIsDownloadable = (registration) => getVerificationRowState(registration) === 'verified';

const MAX_SEPARATE_FILE_DOWNLOADS = 100;

const separateFilesDisabledTitle = (count) => (
  count > MAX_SEPARATE_FILE_DOWNLOADS
    ? `Separate downloads are limited to ${MAX_SEPARATE_FILE_DOWNLOADS} files. Use ZIP archive for ${count} files.`
    : null
);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const downloadRemoteFile = async (url, filename) => {
  const token = localStorage.getItem('token');

  const triggerBlobDownload = (blob, downloadFilename) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  try {
    const response = await axios.get('/api/field-experience-events/download-file', {
      params: { url, filename },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      responseType: 'blob',
    });
    triggerBlobDownload(response.data, filename);
    return true;
  } catch {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('fetch failed');
      const blob = await response.blob();
      triggerBlobDownload(blob, filename);
      return true;
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
      return false;
    }
  }
};

const downloadDocumentsZip = async (folders, zipFilename) => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    '/api/field-experience-events/download-documents-zip',
    { folders, zipFilename },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      responseType: 'blob',
    }
  );

  const blobUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = zipFilename.endsWith('.zip') ? zipFilename : `${zipFilename}.zip`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

const eventInDateRange = (event, startDate, endDate) => {
  if (!startDate && !endDate) return true;
  if (!event.date) return false;
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return false;
  if (startDate) {
    const start = new Date(`${startDate}T00:00:00`);
    if (eventDate < start) return false;
  }
  if (endDate) {
    const end = new Date(`${endDate}T23:59:59.999`);
    if (eventDate > end) return false;
  }
  return true;
};

const getEarliestEventDateInput = (events) => {
  let earliest = null;
  events.forEach((event) => {
    if (!event.date) return;
    const eventDate = new Date(event.date);
    if (Number.isNaN(eventDate.getTime())) return;
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const dateInput = `${year}-${month}-${day}`;
    if (!earliest || dateInput < earliest) earliest = dateInput;
  });
  return earliest || '';
};

const eventMatchesStageFilter = (event, stageFilter) => {
  if (!stageFilter || stageFilter === 'all') return true;
  return event.stage === stageFilter;
};

const MyEvents = ({ userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialUrlParams = useMemo(() => getMyEventsUrlParams(location.search), [location.search]);
  const presentKioskLock = initialUrlParams.presentKiosk;
  const [invitations, setInvitations] = useState([]);
  const [trainerInvitations, setTrainerInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [originalEventUpdatedDates, setOriginalEventUpdatedDates] = useState({});
  const [allEvents, setAllEvents] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [fullDetails, setFullDetails] = useState({});
  const [invitationsOpen, setInvitationsOpen] = useState({});
  const [filteredEventId, setFilteredEventId] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [lastActionEventId, setLastActionEventId] = useState(null);
  const [hasInitialScroll, setHasInitialScroll] = useState(false);
  const [canOpenSAFForm, setCanOpenSAFForm] = useState(false);
  const [canViewAllEvents, setCanViewAllEvents] = useState(false);
  const [canViewRegistrations, setCanViewRegistrations] = useState(false);
  const [canDownloadRegistrations, setCanDownloadRegistrations] = useState(false);
  const [canDownloadDocuments, setCanDownloadDocuments] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [registrationsPopupEvent, setRegistrationsPopupEvent] = useState(null);
  const [registrationsByEvent, setRegistrationsByEvent] = useState({});
  const [loadingRegistrationsEventId, setLoadingRegistrationsEventId] = useState(null);
  const [registrationsError, setRegistrationsError] = useState('');
  const [bulkDownloadOpen, setBulkDownloadOpen] = useState(false);
  const [bulkDownloadTab, setBulkDownloadTab] = useState('events');
  const [bulkDownloadStageFilter, setBulkDownloadStageFilter] = useState('official');
  const [bulkDownloadStartDate, setBulkDownloadStartDate] = useState('');
  const [bulkDownloadEndDate, setBulkDownloadEndDate] = useState('');
  const [bulkSelectedEventIds, setBulkSelectedEventIds] = useState(() => new Set());
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [bulkDownloadError, setBulkDownloadError] = useState('');
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentStartDate, setPresentStartDate] = useState('');
  const [presentEndDate, setPresentEndDate] = useState('');
  const [presentLoading, setPresentLoading] = useState(false);
  const [presentError, setPresentError] = useState('');
  const [presentLoadedEventIds, setPresentLoadedEventIds] = useState(() => new Set());
  const [presentExpandedEventId, setPresentExpandedEventId] = useState(null);
  const [presentLightBackground, setPresentLightBackground] = useState(false);
  const [presentViewMode, setPresentViewMode] = useState('grid');
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [documentsStartDate, setDocumentsStartDate] = useState('');
  const [documentsEndDate, setDocumentsEndDate] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [documentsLoadedEventIds, setDocumentsLoadedEventIds] = useState(() => new Set());
  const [documentsExpandedEventIds, setDocumentsExpandedEventIds] = useState(() => new Set());
  const [documentsSelectedFlierIds, setDocumentsSelectedFlierIds] = useState(() => new Set());
  const [documentsSelectedVerificationKeys, setDocumentsSelectedVerificationKeys] = useState(() => new Set());
  const [documentsBulkDownloading, setDocumentsBulkDownloading] = useState(false);
  const [documentsLightBackground, setDocumentsLightBackground] = useState(false);
  const eventRefs = useRef({});
  const listTopRef = useRef(null);
  const filterMenuRef = useRef(null);

  const officialEvents = useMemo(
    () => allEvents.filter((event) => eventMatchesStageFilter(event, 'official')),
    [allEvents]
  );

  const officialEventIdsKey = useMemo(
    () => officialEvents.map((event) => event.id).sort().join(','),
    [officialEvents]
  );

  // Helper function to check if user has global access on default card
  const hasGlobalAccess = () => {
    if (!userData?.default_accesscard_id) return false;
    const defaultAccessCard = userData?.accesscards?.find(
      ac => ac.accesscard_id === userData.default_accesscard_id
    );
    return defaultAccessCard?.global === true;
  };

  // Helper function to find the nearest event to current date
  const findNearestEventToCurrentDate = (events) => {
    if (!events || events.length === 0) return null;
    
    const currentDate = new Date();
    let nearestEvent = null;
    let smallestDiff = Infinity;
    
    events.forEach(event => {
      const eventDate = new Date(event.date);
      const diff = Math.abs(eventDate.getTime() - currentDate.getTime());
      
      if (diff < smallestDiff) {
        smallestDiff = diff;
        nearestEvent = event;
      }
    });
    
    return nearestEvent;
  };

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (!presentKioskLock) return undefined;
    document.body.classList.add('my-events-present-kiosk');
    return () => document.body.classList.remove('my-events-present-kiosk');
  }, [presentKioskLock]);

  useEffect(() => {
    if (!presentKioskLock || presentOpen || loading || !permissionsLoaded) return;
    if (!canDownloadRegistrations) return;
    if (officialEvents.length === 0) return;
    handleOpenPresent();
  }, [
    presentKioskLock,
    presentOpen,
    loading,
    permissionsLoaded,
    canDownloadRegistrations,
    officialEvents.length,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=my-events', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setCanOpenSAFForm(Boolean(res.data.capabilities?.OpenSAFForm));
          setCanViewAllEvents(Boolean(res.data.capabilities?.ViewAllEvents));
          setCanViewRegistrations(Boolean(res.data.capabilities?.ViewRegistrations));
          setCanDownloadRegistrations(Boolean(res.data.capabilities?.DownloadRegistrations));
          setCanDownloadDocuments(Boolean(res.data.capabilities?.DownloadDocuments));
        }
      } catch {
        if (!cancelled) {
          setCanOpenSAFForm(false);
          setCanViewAllEvents(false);
          setCanViewRegistrations(false);
          setCanDownloadRegistrations(false);
          setCanDownloadDocuments(false);
        }
      } finally {
        if (!cancelled) {
          setPermissionsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check for event ID in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const eventId = urlParams.get('eventId');
    if (eventId) {
      setFilteredEventId(eventId);
    }
  }, [location.search]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load both types of data in parallel
      const [invitationsResponse, trainerInvitationsResponse] = await Promise.all([
        axios.get('/api/field-experience-events/my-invitations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(error => {
          console.error('Error loading invitations:', error);
          return { data: { invitations: [] } };
        }),
        axios.get('/api/field-experience-events/my-training-invitations', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(error => {
          console.error('Error loading trainer invitations:', error);
          return { data: { invitations: [] } };
        })
      ]);

      setInvitations(invitationsResponse.data.invitations);
      setTrainerInvitations(trainerInvitationsResponse.data.invitations);
      
      // Set original event updated dates
      setOriginalEventUpdatedDates(prev => {
        if (Object.keys(prev).length === 0) {
          const map = {};
          invitationsResponse.data.invitations.forEach(inv => {
            if (inv.feevent_id && inv.fe_updateddate) {
              map[inv.feevent_id] = inv.fe_updateddate;
            }
          });
          return map;
        }
        return prev;
      });
      
      console.log('All data loaded:', {
        invitations: invitationsResponse.data.invitations,
        trainerInvitations: trainerInvitationsResponse.data.invitations
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/field-experience-events/my-invitations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.invitations);
      setOriginalEventUpdatedDates(prev => {
        if (Object.keys(prev).length === 0) {
          const map = {};
          response.data.invitations.forEach(inv => {
            if (inv.feevent_id && inv.fe_updateddate) {
              map[inv.feevent_id] = inv.fe_updateddate;
            }
          });
          return map;
        }
        return prev;
      });
      console.log('Invitations loaded:', response.data.invitations);
    } catch (error) {
      console.error('Error loading invitations:', error);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const loadTrainerInvitations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/field-experience-events/my-training-invitations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainerInvitations(response.data.invitations);
    } catch (error) {
      setTrainerInvitations([]);
    }
  };

  useEffect(() => {
    const eventMap = new Map();
    
    // Add events from coordinator invitations (these have complete event info)
    invitations.forEach(inv => {
      if (!eventMap.has(inv.feevent_id)) {
        eventMap.set(inv.feevent_id, {
          id: inv.feevent_id,
          title: inv.title,
          date: inv.date,
          description: inv.description,
          type: inv.type,
          stage: inv.stage,
          status: inv.status,
          coverPhoto: inv.coverphoto || inv.coverPhoto || '',
          first_name: inv.first_name,
          last_name: inv.last_name,
          primary_email: inv.primary_email,
          schools: inv.schools,
          allowallschools: inv.allowallschools,
          leadinstructor: inv.leadinstructor,
          coordinatorInvitation: inv,
          trainerInvitations: []
        });
      }
    });
    
    // Add events from trainer invitations (may need to fetch complete event info)
    trainerInvitations.forEach(tp => {
      if (!eventMap.has(tp.feevent_id)) {
        // This event only has trainer invitations, so we need to fetch complete event info
        eventMap.set(tp.feevent_id, {
          id: tp.feevent_id,
          title: tp.event_title,
          date: tp.event_date,
          description: tp.event_description || '',
          type: tp.event_type || '',
          stage: tp.event_stage,
          status: tp.event_status,
          coverPhoto: tp.event_coverphoto || tp.coverphoto || '',
          first_name: tp.lead_instructor_first_name || '', // Use from trainer invitation if available
          last_name: tp.lead_instructor_last_name || '', // Use from trainer invitation if available
          primary_email: tp.lead_instructor_email || '', // Use from trainer invitation if available
          coordinatorInvitation: null,
          trainerInvitations: [tp]
        });
      } else {
        const existing = eventMap.get(tp.feevent_id);
        if (!existing.coverPhoto && (tp.event_coverphoto || tp.coverphoto)) {
          existing.coverPhoto = tp.event_coverphoto || tp.coverphoto;
        }
        existing.trainerInvitations.push(tp);
      }
    });
    
    // Convert to array and sort by date (newest first)
    const sortedEvents = Array.from(eventMap.values()).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Descending order (newest first)
    });
    
    setAllEvents(sortedEvents);
  }, [invitations, trainerInvitations]);

  // Scroll to filtered event when it becomes available
  useEffect(() => {
    if (filteredEventId && allEvents.length > 0) {
      const eventExists = allEvents.find(event => event.id === filteredEventId);
      if (eventExists && eventRefs.current[filteredEventId]) {
        setTimeout(() => {
          eventRefs.current[filteredEventId].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [filteredEventId, allEvents]);

  // Initial scroll to nearest event on page load (only once)
  useEffect(() => {
    if (!hasInitialScroll && allEvents.length > 0 && !loading) {
      const nearestEvent = findNearestEventToCurrentDate(allEvents);
      if (nearestEvent && eventRefs.current[nearestEvent.id]) {
        setTimeout(() => {
          eventRefs.current[nearestEvent.id].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 200);
        setHasInitialScroll(true);
      }
    }
  }, [allEvents, loading, hasInitialScroll]);

  const filterOptions = [
    { value: '', label: 'No Filter' },
    { value: 'official', label: 'Official Events', condition: (event) => event.stage === 'official' },
    { value: 'saf-pending', label: 'SAF Submitted', condition: (event) => event.stage === 'saf' && event.status === 'pending' },
    { value: 'request-approved', label: 'Waiting for SAF to be Submitted', condition: (event) => event.stage === 'request' && event.status === 'approved' },
    { value: 'request-pending', label: 'Request Submitted', condition: (event) => event.stage === 'request' && event.status === 'pending' },
    { value: 'request-denied', label: 'Denied Requests', condition: (event) => event.stage === 'request' && event.status === 'denied' },
    { value: 'saf-denied', label: 'Denied SAF', condition: (event) => event.stage === 'saf' && event.status === 'denied' }
  ];

  const getFilteredEvents = () => {
    if (!appliedFilter) {
      return allEvents;
    }
    
    const filterOption = filterOptions.find(option => option.value === appliedFilter);
    if (!filterOption || !filterOption.condition) {
      return allEvents;
    }
    
    return allEvents.filter(filterOption.condition);
  };

  const getDisplayEvents = () => {
    const filtered = getFilteredEvents();
    if (filteredEventId) {
      return filtered.filter(event => event.id === filteredEventId);
    }
    return filtered;
  };

  const handleFilterSelect = (value) => {
    setAppliedFilter(value);
    setLastActionEventId(null);
    setHasInitialScroll(true);
    setFilterMenuOpen(false);
  };

  const activeFilterLabel = filterOptions.find(option => option.value === appliedFilter)?.label ?? 'No Filter';

  useEffect(() => {
    if (!filterMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setFilterMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterMenuOpen]);

  useEffect(() => {
    if (!helpOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [helpOpen]);

  useEffect(() => {
    if (!presentOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (presentExpandedEventId) {
          setPresentExpandedEventId(null);
        } else if (!presentKioskLock) {
          setPresentOpen(false);
        }
        return;
      }

      if (presentExpandedEventId && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        navigatePresentDetail(event.key === 'ArrowRight' ? 'next' : 'prev');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [presentOpen, presentExpandedEventId, presentKioskLock, allEvents, presentStartDate, presentEndDate, registrationsByEvent]);

  useEffect(() => {
    if (!presentOpen || !presentExpandedEventId) return undefined;

    let lastNavTime = 0;
    const WHEEL_COOLDOWN_MS = 450;
    const WHEEL_THRESHOLD = 12;
    const SWIPE_THRESHOLD = 50;

    const handleWheel = (event) => {
      event.preventDefault();
      const now = Date.now();
      if (now - lastNavTime < WHEEL_COOLDOWN_MS) return;
      if (Math.abs(event.deltaY) < WHEEL_THRESHOLD) return;
      lastNavTime = now;
      navigatePresentDetail(event.deltaY > 0 ? 'next' : 'prev');
    };

    let touchStartY = null;
    let touchStartX = null;

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        touchStartY = null;
        return;
      }
      touchStartY = event.touches[0].clientY;
      touchStartX = event.touches[0].clientX;
    };

    const handleTouchMove = (event) => {
      if (touchStartY === null) return;
      event.preventDefault();
    };

    const handleTouchEnd = (event) => {
      if (touchStartY === null) return;
      const touch = event.changedTouches[0];
      const deltaY = touch.clientY - touchStartY;
      const deltaX = touch.clientX - touchStartX;
      touchStartY = null;
      touchStartX = null;

      if (Math.abs(deltaY) < SWIPE_THRESHOLD || Math.abs(deltaY) < Math.abs(deltaX)) return;

      const now = Date.now();
      if (now - lastNavTime < WHEEL_COOLDOWN_MS) return;
      lastNavTime = now;
      navigatePresentDetail(deltaY < 0 ? 'next' : 'prev');
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [presentOpen, presentExpandedEventId, allEvents, presentStartDate, presentEndDate, registrationsByEvent]);

  useEffect(() => {
    if (!presentOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [presentOpen]);

  const handleInvolvement = async (eventId, involved) => {
    try {
      setActionLoading(prev => ({ ...prev, [`involvement-${eventId}-${involved}`]: true }));
      const token = localStorage.getItem('token');

      const invitation = invitations.find(inv => inv.feevent_id === eventId);
      const expectedUpdatedDate = invitation ? invitation.updateddate : null;

      await axios.patch(`/api/field-experience-events/invitations/${eventId}/involvement`,
        { involved, expectedUpdatedDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await loadInvitations();
      
      // Set the last action event ID and scroll to the card
      setLastActionEventId(eventId);
      setHasInitialScroll(true); // Prevent initial scroll from interfering
      setTimeout(() => {
        if (eventRefs.current[eventId]) {
          eventRefs.current[eventId].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
      
      // Clear the last action indicator after 5 seconds
      setTimeout(() => {
        setLastActionEventId(null);
      }, 5000);
    } catch (error) {
      console.error('Error updating involvement:', error);

      if (error.response && error.response.status === 409) {
        setError(`⚠️ ${error.response.data.error}`);
        setTimeout(async () => {
          await loadInvitations();
          const token = localStorage.getItem('token');
          const response = await axios.get('/api/field-experience-events/my-invitations', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOriginalEventUpdatedDates(prev => {
            const map = { ...prev };
            response.data.invitations.forEach(inv => {
              if (inv.feevent_id && inv.fe_updateddate) {
                map[inv.feevent_id] = inv.fe_updateddate;
              }
            });
            return map;
          });
        }, 2000);
      } else if (error.response && error.response.status === 400) {
        setError(error.response.data.error);
        await loadInvitations();
      } else {
        setError('Failed to update involvement');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [`involvement-${eventId}-${involved}`]: false }));
    }
  };

  const handleDecision = async (eventId, decision, forceApprove = false) => {
    try {
      const loadingKey = forceApprove ? `decision-${eventId}-force-approve` : `decision-${eventId}-${decision}`;
      setActionLoading(prev => ({ ...prev, [loadingKey]: true }));
      const token = localStorage.getItem('token');

      const invitation = invitations.find(inv => inv.feevent_id === eventId);
      const expectedUpdatedDate = invitation ? invitation.updateddate : null;
      const expectedEventUpdatedDate = originalEventUpdatedDates[eventId] || null;
      const isSafOrOfficialStage = invitation && (invitation.stage === 'saf' || invitation.stage === 'official');

      if (forceApprove) {
        await axios.patch(`/api/field-experience-events/invitations/${eventId}/force-approve`,
          isSafOrOfficialStage ? { expectedUpdatedDate, expectedEventUpdatedDate } : { expectedUpdatedDate },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.patch(`/api/field-experience-events/invitations/${eventId}/decision`,
          isSafOrOfficialStage ? { decision, forceApprove, expectedUpdatedDate, expectedEventUpdatedDate } : { decision, forceApprove, expectedUpdatedDate },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      await loadInvitations();
      
      // Set the last action event ID and scroll to the card
      setLastActionEventId(eventId);
      setHasInitialScroll(true); // Prevent initial scroll from interfering
      setTimeout(() => {
        if (eventRefs.current[eventId]) {
          eventRefs.current[eventId].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
      
      // Clear the last action indicator after 5 seconds
      setTimeout(() => {
        setLastActionEventId(null);
      }, 5000);
    } catch (error) {
      console.error('Error making decision:', error);

      if (error.response && error.response.status === 409) {
        setError(`⚠️ ${error.response.data.error}`);
        setTimeout(async () => {
          await loadInvitations();
          const token = localStorage.getItem('token');
          const response = await axios.get('/api/field-experience-events/my-invitations', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOriginalEventUpdatedDates(prev => {
            const map = { ...prev };
            response.data.invitations.forEach(inv => {
              if (inv.feevent_id && inv.fe_updateddate) {
                map[inv.feevent_id] = inv.fe_updateddate;
              }
            });
            return map;
          });
        }, 2000);
      } else if (error.response && error.response.status === 400) {
        setError(error.response.data.error);
        await loadInvitations();
      } else {
        setError('Failed to make decision');
      }
    } finally {
      const loadingKey = forceApprove ? `decision-${eventId}-force-approve` : `decision-${eventId}-${decision}`;
      setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const getSafFormHref = (eventId) => `/saf-form/${eventId}`;

  const handleOpenTrainingPlan = (eventId, trainingPlanId) => {
    if (!eventId || !trainingPlanId) {
      return;
    }

    navigate(`/saf-form/${eventId}?mode=trainingplan&trainingplan_id=${trainingPlanId}`);
  };

  const getTrainingPlanHref = (eventId, trainingPlanId) =>
    `/saf-form/${eventId}?mode=trainingplan&trainingplan_id=${trainingPlanId}`;

  const getAcceptedTrainerInvitations = (event) =>
    event.trainerInvitations.filter(tp => tp.involved === true);

  const hasMultipleAcceptedTrainerInvitations = (event) =>
    getAcceptedTrainerInvitations(event).length > 1;

  const handleHammerClick = (eventId, event, e) => {
    e.stopPropagation();
    const accepted = getAcceptedTrainerInvitations(event);

    if (accepted.length === 1) {
      handleOpenTrainingPlan(eventId, accepted[0].id);
    } else if (accepted.length > 1) {
      setOpenRows(prev => ({ ...prev, [eventId]: true }));
      setInvitationsOpen(prev => ({ ...prev, [eventId]: true }));
    }

    if (e.currentTarget.blur) {
      e.currentTarget.blur();
    }
  };

  const isButtonLoading = (eventId, decision) => {
    return actionLoading[`decision-${eventId}-${decision}`];
  };

  const isAnyButtonLoading = (eventId) => {
    return Object.keys(actionLoading).some(key =>
      (key.includes(`decision-${eventId}`) && actionLoading[key]) ||
      (key.includes(`involvement-${eventId}`) && actionLoading[key])
    );
  };

  const isInvolvementButtonLoading = (eventId, involved) => {
    return actionLoading[`involvement-${eventId}-${involved}`];
  };

  const isAnyInvolvementButtonLoading = (eventId) => {
    return isInvolvementButtonLoading(eventId, true) || isInvolvementButtonLoading(eventId, false);
  };

  const getStageDisplayLabel = (stage) => {
    switch (stage) {
      case 'request': return 'Request';
      case 'saf': return 'SAF';
      case 'official': return 'Official';
      default: return stage;
    }
  };

  const getStageBadgeStyle = (stage) => {
    switch (stage) {
      case 'request': return { backgroundColor: '#eff6ff', color: '#1d4e89', borderColor: '#bfdbfe' };
      case 'saf': return { backgroundColor: '#ecfeff', color: '#0e7490', borderColor: '#a5f3fc' };
      case 'official': return { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#bbf7d0' };
      default: return { backgroundColor: '#f8fafc', color: '#64748b', borderColor: '#e2e8f0' };
    }
  };

  const getOfficialStatusBadgeStyle = (status) => {
    switch (status) {
      case 'hidden': return { backgroundColor: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' };
      case 'locked': return { backgroundColor: '#e2e8f0', color: '#475569', borderColor: '#cbd5e1' };
      case 'live': return { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#bbf7d0' };
      default: return { backgroundColor: '#f8fafc', color: '#64748b', borderColor: '#e2e8f0' };
    }
  };

  const getStatusBadgeStyle = (status, stage) => {
    if (stage === 'official') return getOfficialStatusBadgeStyle(status);
    switch (status) {
      case 'pending': return { backgroundColor: '#fffbeb', color: '#b45309', borderColor: '#fde68a' };
      case 'approved': return { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#bbf7d0' };
      case 'denied': return { backgroundColor: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };
      default: return { backgroundColor: '#f8fafc', color: '#64748b', borderColor: '#e2e8f0' };
    }
  };

  const canMakeDecision = (invitation) => {
    const isRequestStage = invitation.stage === 'request';
    const currentDecision = isRequestStage ? invitation.requestdecision : invitation.safdecision;

    return invitation.involved &&
      !currentDecision &&
      invitation.status !== 'approved';
  };

  const canForceApprove = (invitation) => {
    return invitation.involved &&
      invitation.status === 'pending' &&
      invitation.requestdecision === 'approved' &&
      invitation.allCoordinatorDecisions &&
      invitation.allCoordinatorDecisions.filter(d => d.requestdecision === 'approved').length === 1;
  };

  const canChangeDecision = (invitation) => {
    const isRequestStage = invitation.stage === 'request';
    const currentDecision = isRequestStage ? invitation.requestdecision : invitation.safdecision;

    return invitation.involved &&
      currentDecision &&
      invitation.status !== 'approved';
  };

  const shouldShowDecisionButtons = (invitation) => {
    return canMakeDecision(invitation) || canChangeDecision(invitation);
  };

  const canFillOutSAF = (invitation) => {
    return invitation.stage === 'request' &&
      invitation.status === 'approved' &&
      invitation.leadinstructor === userData.account_id;
  };

  const isCoordinator = (invitation) => {
    return invitation.account_id === userData.account_id;
  };

  const isLeadInstructor = (invitation) => {
    return invitation.leadinstructor === userData.account_id;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}…`;
  };

  const isDescriptionTruncated = (description) => Boolean(description && description.length > 100);

  const handleRowClick = (eventId, event) => {
    if (openRows[eventId]) {
      setOpenRows(prev => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      setFullDetails(prev => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      setInvitationsOpen(prev => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
    } else {
      setOpenRows(prev => ({ ...prev, [eventId]: true }));
      if (hasInvitationsPanel(event)) {
        setInvitationsOpen(prev => ({ ...prev, [eventId]: true }));
      }
    }
  };

  const toggleFullDetails = (eventId, e) => {
    e.stopPropagation();
    setFullDetails(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const toggleInvitations = (eventId, e) => {
    e.stopPropagation();
    setOpenRows(prev => ({ ...prev, [eventId]: true }));
    setInvitationsOpen(prev => ({ ...prev, [eventId]: !prev[eventId] }));
    if (e.currentTarget.blur) {
      e.currentTarget.blur();
    }
  };

  const handleIconButtonKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action(e);
    }
  };

  const getEventTitle = (event) => {
    return event.title && event.title.trim() !== '' ? event.title : 'Field Experience Event';
  };

  const isEventFiltered = (eventId) => {
    return filteredEventId === eventId;
  };

  const clearFilter = () => {
    setFilteredEventId(null);
    setAppliedFilter('');
    // Remove the eventId parameter from URL
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('eventId');
    navigate(`${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`);
  };

  const canShowCoordinatorActions = (invitation) => {
    return isCoordinator(invitation);
  };

  const canShowLeadInstructorActions = (invitation) => {
    return isLeadInstructor(invitation);
  };

  const getCoordinatorStatus = (invitation) => {
    if (!isCoordinator(invitation)) {
      return { involved: null, requestdecision: null, safdecision: null };
    }
    return {
      involved: invitation.involved,
      requestdecision: invitation.requestdecision,
      safdecision: invitation.safdecision
    };
  };

  const hasInvitationSafAccess = (event) => {
    const inv = event.coordinatorInvitation;
    if (!inv) return false;
    if (canFillOutSAF(inv)) return true;
    const coordinatorStatus = getCoordinatorStatus(inv);
    return (
      (canShowLeadInstructorActions(inv) ||
        (canShowCoordinatorActions(inv) && coordinatorStatus.involved)) &&
      (event.stage === 'saf' || event.stage === 'official')
    );
  };

  const canShowSafIcon = (event) =>
    hasInvitationSafAccess(event) || canMyEventsOpenSAFForm(event, canOpenSAFForm, userData);

  const getSafEditTitle = (event) => {
    const inv = event.coordinatorInvitation;
    if (inv && canFillOutSAF(inv)) return 'Fill Out SAF';
    if (hasInvitationSafAccess(event)) return 'Edit SAF';
    return 'Open SAF';
  };

  const hasCoordinatorSafInvitationNote = (event) => {
    const inv = event.coordinatorInvitation;
    if (!inv || !canShowCoordinatorActions(inv)) return false;
    const cs = getCoordinatorStatus(inv);
    return cs.involved === true && hasInvitationSafAccess(event);
  };

  const coordinatorHasPendingAction = (event) => {
    const inv = event.coordinatorInvitation;
    if (!inv || !canShowCoordinatorActions(inv)) return false;
    const cs = getCoordinatorStatus(inv);
    if (cs.involved === null && cs.requestdecision === null) return true;
    if (cs.involved === false) return true;
    if (event.stage === 'request' && shouldShowDecisionButtons(inv)) return true;
    if (event.stage === 'request' && canForceApprove(inv)) return true;
    return false;
  };

  const trainerHasPendingAction = (tp) => tp.involved === null;

  const getPendingInvitationActionCount = (event) => {
    let count = event.trainerInvitations.filter(trainerHasPendingAction).length;
    if (coordinatorHasPendingAction(event)) count += 1;
    return count;
  };

  const getInvitationsIconLabel = (event) => {
    const pending = getPendingInvitationActionCount(event);
    if (pending === 0) return 'Invitations';
    const noun = pending === 1 ? 'invitation needs' : 'invitations need';
    return `Invitations — ${pending} ${noun} your response`;
  };

  const getRegistrationAttendanceLabel = (registration) => {
    if (registration.attended === null) return 'Not marked';
    if (registration.attended === false) return 'Absent';
    return registration.attendance ? `Present (${registration.attendance})` : 'Present';
  };

  const getRegistrationHoursVerifiedLabel = (registration) => {
    if (registration.status === 'Verified') {
      return registration.hours != null ? `Yes (${registration.hours}h)` : 'Yes';
    }
    if (registration.status === 'Pending') return 'Pending';
    return '—';
  };

  const loadRegistrationsForEvents = async (eventIds, { updateCache = true } = {}) => {
    const uniqueIds = [...new Set(eventIds.filter(Boolean))];
    if (uniqueIds.length === 0) return {};

    const token = localStorage.getItem('token');
    const res = await axios.post(
      '/api/feeventregistrations/by-events',
      { feevent_ids: uniqueIds },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const allRegistrations = res.data.registrations || [];
    const byEvent = Object.fromEntries(uniqueIds.map((id) => [id, []]));
    allRegistrations.forEach((registration) => {
      const eventId = registration.feevent_id;
      if (byEvent[eventId]) {
        byEvent[eventId].push(registration);
      }
    });

    if (updateCache) {
      setRegistrationsByEvent((prev) => ({ ...prev, ...byEvent }));
    }
    return byEvent;
  };

  const loadRegistrationsForEvent = async (eventId, { updateCache = true } = {}) => {
    const byEvent = await loadRegistrationsForEvents([eventId], { updateCache });
    return byEvent[eventId] || [];
  };

  const fetchEventRegistrations = async (eventId) => {
    setLoadingRegistrationsEventId(eventId);
    setRegistrationsError('');
    try {
      await loadRegistrationsForEvent(eventId);
    } catch (err) {
      console.error('Error fetching registrations:', err);
      setRegistrationsError('Failed to load registrations for this event.');
    } finally {
      setLoadingRegistrationsEventId(null);
    }
  };

  const handleDownloadRegistrationsCsv = (event, registrations) => {
    const rows = registrations.map((reg) => registrationToCsvRow(reg, event));
    const datePart = new Date().toISOString().split('T')[0];
    downloadRegistrationsCsv(rows, `registrations-${slugifyFilename(event.title)}-${datePart}.csv`);
  };

  const handleOpenBulkDownload = () => {
    setHelpOpen(false);
    setFilterMenuOpen(false);
    setRegistrationsPopupEvent(null);
    setDocumentsOpen(false);
    setBulkDownloadError('');
    setBulkDownloadTab('events');
    setBulkDownloadStageFilter('official');
    setBulkDownloadStartDate('');
    setBulkDownloadEndDate('');
    const defaultEvents = allEvents.filter(
      (event) => eventMatchesStageFilter(event, 'official') && eventInDateRange(event, '', '')
    );
    setBulkSelectedEventIds(new Set(defaultEvents.map((event) => event.id)));
    setBulkDownloadOpen(true);
  };

  const handleCloseBulkDownload = () => {
    if (bulkDownloadLoading) return;
    setBulkDownloadOpen(false);
    setBulkDownloadError('');
  };

  const getBulkDownloadVisibleEvents = () => (
    allEvents.filter(
      (event) => eventInDateRange(event, bulkDownloadStartDate, bulkDownloadEndDate)
        && eventMatchesStageFilter(event, bulkDownloadStageFilter)
    )
  );

  const handleBulkSelectAllVisible = () => {
    setBulkSelectedEventIds((prev) => {
      const next = new Set(prev);
      getBulkDownloadVisibleEvents().forEach((event) => next.add(event.id));
      return next;
    });
  };

  const handleBulkDeselectAllVisible = () => {
    setBulkSelectedEventIds((prev) => {
      const next = new Set(prev);
      getBulkDownloadVisibleEvents().forEach((event) => next.delete(event.id));
      return next;
    });
  };

  const toggleBulkDownloadEvent = (eventId) => {
    setBulkSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const handleBulkDownloadRegistrationsConfirm = async () => {
    const selectedEvents = getBulkDownloadVisibleEvents().filter((event) => bulkSelectedEventIds.has(event.id));
    if (selectedEvents.length === 0) {
      setBulkDownloadError('Select at least one event to download.');
      return;
    }

    setBulkDownloadLoading(true);
    setBulkDownloadError('');
    try {
      const registrationsBySelectedEvent = await loadRegistrationsForEvents(
        selectedEvents.map((event) => event.id)
      );
      const allRows = [];
      selectedEvents.forEach((event) => {
        (registrationsBySelectedEvent[event.id] || []).forEach((reg) => {
          allRows.push(registrationToCsvRow(reg, event));
        });
      });

      const datePart = new Date().toISOString().split('T')[0];
      downloadRegistrationsCsv(allRows, `registrations-bulk-${datePart}.csv`);
      setBulkDownloadOpen(false);
    } catch (err) {
      console.error('Error downloading registrations:', err);
      setBulkDownloadError('Failed to download registrations. Please try again.');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  const handleBulkDownloadEventDataConfirm = async () => {
    const selectedEvents = getBulkDownloadVisibleEvents().filter((event) => bulkSelectedEventIds.has(event.id));
    if (selectedEvents.length === 0) {
      setBulkDownloadError('Select at least one event to download.');
      return;
    }

    setBulkDownloadLoading(true);
    setBulkDownloadError('');
    try {
      const registrationsBySelectedEvent = await loadRegistrationsForEvents(
        selectedEvents.map((event) => event.id)
      );
      const allRows = selectedEvents.map((event) => {
        const registrations = registrationsBySelectedEvent[event.id] || [];
        return eventToCsvRow(event, computeEventRegistrationStats(registrations));
      });

      const datePart = new Date().toISOString().split('T')[0];
      downloadEventsCsv(allRows, `event-data-${datePart}.csv`);
      setBulkDownloadOpen(false);
    } catch (err) {
      console.error('Error downloading event data:', err);
      setBulkDownloadError('Failed to download event data. Please try again.');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  const handleBulkDownloadConfirm = () => {
    if (bulkDownloadTab === 'events') {
      handleBulkDownloadEventDataConfirm();
      return;
    }
    handleBulkDownloadRegistrationsConfirm();
  };

  const getOfficialEvents = () => officialEvents;

  const getPresentVisibleEvents = () => (
    officialEvents.filter((event) => eventInDateRange(event, presentStartDate, presentEndDate))
  );

  useEffect(() => {
    if (!presentOpen || !officialEventIdsKey) return undefined;

    const officialIds = officialEventIdsKey.split(',').filter(Boolean);
    const missingIds = officialIds.filter((id) => !presentLoadedEventIds.has(id));
    if (missingIds.length === 0) return undefined;

    let cancelled = false;

    (async () => {
      setPresentLoading(true);
      setPresentError('');
      try {
        await loadRegistrationsForEvents(missingIds);
        if (cancelled) return;
        setPresentLoadedEventIds((prev) => {
          const next = new Set(prev);
          missingIds.forEach((id) => next.add(id));
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading analytics data:', err);
        setPresentError('Failed to load analytics data. Please try again.');
      } finally {
        if (!cancelled) {
          setPresentLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [presentOpen, officialEventIdsKey, presentLoadedEventIds]);

  const getDocumentsVisibleEvents = () => (
    officialEvents.filter((event) => eventInDateRange(event, documentsStartDate, documentsEndDate))
  );

  useEffect(() => {
    if (!documentsOpen || !officialEventIdsKey) return undefined;

    const visibleIds = getDocumentsVisibleEvents().map((event) => event.id);
    const missingIds = visibleIds.filter((id) => !documentsLoadedEventIds.has(id));
    if (missingIds.length === 0) return undefined;

    let cancelled = false;

    (async () => {
      setDocumentsLoading(true);
      setDocumentsError('');
      try {
        await loadRegistrationsForEvents(missingIds);
        if (cancelled) return;
        setDocumentsLoadedEventIds((prev) => {
          const next = new Set(prev);
          missingIds.forEach((id) => next.add(id));
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading documents data:', err);
        setDocumentsError('Failed to load registration data. Please try again.');
      } finally {
        if (!cancelled) {
          setDocumentsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentsOpen, officialEventIdsKey, documentsLoadedEventIds, documentsStartDate, documentsEndDate]);

  const handleOpenDocuments = () => {
    setHelpOpen(false);
    setFilterMenuOpen(false);
    setRegistrationsPopupEvent(null);
    setBulkDownloadOpen(false);
    setPresentOpen(false);
    setDocumentsError('');
    setDocumentsStartDate(getEarliestEventDateInput(officialEvents));
    setDocumentsEndDate('');
    setDocumentsExpandedEventIds(new Set());
    setDocumentsSelectedFlierIds(new Set());
    setDocumentsSelectedVerificationKeys(new Set());
    setDocumentsLightBackground(false);
    setDocumentsOpen(true);
  };

  const handleCloseDocuments = () => {
    if (documentsBulkDownloading) return;
    setDocumentsOpen(false);
    setDocumentsExpandedEventIds(new Set());
    setDocumentsError('');
  };

  const toggleDocumentsEventExpanded = (eventId) => {
    setDocumentsExpandedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const toggleDocumentsFlierSelection = (eventId) => {
    setDocumentsSelectedFlierIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const toggleDocumentsVerificationSelection = (eventId, registrationId) => {
    const key = verificationSelectionKey(eventId, registrationId);
    setDocumentsSelectedVerificationKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllDocumentsFliers = () => {
    const next = new Set();
    getDocumentsVisibleEvents().forEach((event) => {
      if (getEventFlierUrl(event)) next.add(event.id);
    });
    setDocumentsSelectedFlierIds(next);
  };

  const deselectAllDocumentsFliers = () => {
    setDocumentsSelectedFlierIds(new Set());
  };

  const getDownloadableVerificationsForEvent = (event) => (
    (registrationsByEvent[event.id] || []).filter(registrationIsDownloadable)
  );

  const getAllDownloadableVerifications = (events = getDocumentsVisibleEvents()) => {
    const items = [];
    events.forEach((event) => {
      getDownloadableVerificationsForEvent(event).forEach((registration) => {
        items.push({ event, registration });
      });
    });
    return items;
  };

  const selectAllDocumentsVerifications = (events = getDocumentsVisibleEvents()) => {
    const next = new Set();
    getAllDownloadableVerifications(events).forEach(({ event, registration }) => {
      next.add(verificationSelectionKey(event.id, registration.id));
    });
    setDocumentsSelectedVerificationKeys(next);
  };

  const deselectAllDocumentsVerifications = () => {
    setDocumentsSelectedVerificationKeys(new Set());
  };

  const handleDownloadEventFlier = async (event) => {
    const flierUrl = getEventFlierUrl(event);
    if (!flierUrl) return;
    await downloadRemoteFile(flierUrl, buildFlierFilename(event));
  };

  const handleDownloadVerification = async (event, registration) => {
    if (!registrationIsDownloadable(registration)) return;
    await downloadRemoteFile(registration.pdflink, buildVerificationFilename(event, registration));
  };

  const downloadDocumentsZipFromFolders = async (folders, zipBasename) => {
    const datePart = new Date().toISOString().split('T')[0];
    const zipFilename = `${zipBasename}-${datePart}.zip`;
    await downloadDocumentsZip(folders, zipFilename);
  };

  const handleBulkDownloadDocumentsFliersZip = async () => {
    const events = getDocumentsVisibleEvents().filter(
      (event) => documentsSelectedFlierIds.has(event.id) && getEventFlierUrl(event)
    );
    const folders = events
      .map((event) => buildEventDocumentsFolder(event, { includeFlier: true }))
      .filter(Boolean);

    if (folders.length === 0) {
      setDocumentsError('Select at least one event flier to download.');
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      await downloadDocumentsZipFromFolders(folders, 'event-fliers');
    } catch (err) {
      console.error('Error downloading flier zip:', err);
      setDocumentsError('Failed to create flier zip. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const handleBulkDownloadDocumentsVerificationsZip = async () => {
    const folders = [];
    getDocumentsVisibleEvents().forEach((event) => {
      const registrations = getDownloadableVerificationsForEvent(event).filter((registration) => (
        documentsSelectedVerificationKeys.has(verificationSelectionKey(event.id, registration.id))
      ));
      const folder = buildEventDocumentsFolder(event, { includeFlier: false, registrations });
      if (folder) folders.push(folder);
    });

    if (folders.length === 0) {
      setDocumentsError('Select at least one verified hour verification to download.');
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      await downloadDocumentsZipFromFolders(folders, 'hour-verifications');
    } catch (err) {
      console.error('Error downloading verification zip:', err);
      setDocumentsError('Failed to create verification zip. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const handleBulkDownloadDocumentsCombinedZip = async () => {
    const folders = [];
    getDocumentsVisibleEvents().forEach((event) => {
      const includeFlier = documentsSelectedFlierIds.has(event.id) && Boolean(getEventFlierUrl(event));
      const registrations = getDownloadableVerificationsForEvent(event).filter((registration) => (
        documentsSelectedVerificationKeys.has(verificationSelectionKey(event.id, registration.id))
      ));
      if (!includeFlier && registrations.length === 0) return;
      const folder = buildEventDocumentsFolder(event, { includeFlier, registrations });
      if (folder) folders.push(folder);
    });

    if (folders.length === 0) {
      setDocumentsError('Select at least one flier or verified hour verification to download.');
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      await downloadDocumentsZipFromFolders(folders, 'event-documents');
    } catch (err) {
      console.error('Error downloading documents zip:', err);
      setDocumentsError('Failed to create documents zip. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const handleDownloadEventDocumentsZip = async (event) => {
    const downloadable = getDownloadableVerificationsForEvent(event);
    const selected = downloadable.filter((registration) => (
      documentsSelectedVerificationKeys.has(verificationSelectionKey(event.id, registration.id))
    ));
    const registrations = selected.length > 0 ? selected : downloadable;
    const folder = buildEventDocumentsFolder(event, { includeFlier: true, registrations });

    if (!folder) {
      setDocumentsError('No flier or verified forms are available for this event.');
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      await downloadDocumentsZipFromFolders([folder], buildEventFolderName(event));
    } catch (err) {
      console.error('Error downloading event zip:', err);
      setDocumentsError('Failed to create event folder zip. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const handleBulkDownloadDocumentsFliers = async () => {
    const events = getDocumentsVisibleEvents().filter(
      (event) => documentsSelectedFlierIds.has(event.id) && getEventFlierUrl(event)
    );
    if (events.length === 0) {
      setDocumentsError('Select at least one event flier to download.');
      return;
    }
    if (events.length > MAX_SEPARATE_FILE_DOWNLOADS) {
      setDocumentsError(`Separate file downloads are limited to ${MAX_SEPARATE_FILE_DOWNLOADS}. Use ZIP archive instead.`);
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      for (let index = 0; index < events.length; index += 1) {
        await handleDownloadEventFlier(events[index]);
        if (index < events.length - 1) await delay(350);
      }
    } catch (err) {
      console.error('Error downloading fliers:', err);
      setDocumentsError('Some fliers could not be downloaded. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const handleBulkDownloadDocumentsVerifications = async () => {
    const items = [];
    getDocumentsVisibleEvents().forEach((event) => {
      getDownloadableVerificationsForEvent(event).forEach((registration) => {
        const key = verificationSelectionKey(event.id, registration.id);
        if (documentsSelectedVerificationKeys.has(key)) {
          items.push({ event, registration });
        }
      });
    });

    if (items.length === 0) {
      setDocumentsError('Select at least one verified hour verification to download.');
      return;
    }
    if (items.length > MAX_SEPARATE_FILE_DOWNLOADS) {
      setDocumentsError(`Separate file downloads are limited to ${MAX_SEPARATE_FILE_DOWNLOADS}. Use ZIP archive instead.`);
      return;
    }

    setDocumentsBulkDownloading(true);
    setDocumentsError('');
    try {
      for (let index = 0; index < items.length; index += 1) {
        const { event, registration } = items[index];
        await handleDownloadVerification(event, registration);
        if (index < items.length - 1) await delay(350);
      }
    } catch (err) {
      console.error('Error downloading verifications:', err);
      setDocumentsError('Some verification forms could not be downloaded. Please try again.');
    } finally {
      setDocumentsBulkDownloading(false);
    }
  };

  const renderDocumentsOverlay = () => {
    if (!documentsOpen) return null;

    const visibleEvents = getDocumentsVisibleEvents();
    const flierCount = visibleEvents.filter((event) => getEventFlierUrl(event)).length;
    const downloadableVerifications = getAllDownloadableVerifications();
    const selectedFlierCount = visibleEvents.filter(
      (event) => documentsSelectedFlierIds.has(event.id) && getEventFlierUrl(event)
    ).length;
    const selectedVerificationCount = downloadableVerifications.filter(
      ({ event, registration }) => documentsSelectedVerificationKeys.has(
        verificationSelectionKey(event.id, registration.id)
      )
    ).length;
    const selectedCombinedCount = visibleEvents.filter((event) => {
      const hasFlier = documentsSelectedFlierIds.has(event.id) && Boolean(getEventFlierUrl(event));
      const hasVerification = getDownloadableVerificationsForEvent(event).some((registration) => (
        documentsSelectedVerificationKeys.has(verificationSelectionKey(event.id, registration.id))
      ));
      return hasFlier || hasVerification;
    }).length;
    const fliersSeparateFilesDisabled = selectedFlierCount > MAX_SEPARATE_FILE_DOWNLOADS;
    const verificationsSeparateFilesDisabled = selectedVerificationCount > MAX_SEPARATE_FILE_DOWNLOADS;

    return (
      <div
        className={`my-events-present-layer ${documentsLightBackground ? 'my-events-present-layer--light' : 'my-events-present-layer--dark'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Event documents library"
      >
        <button
          type="button"
          className="my-events-present-close"
          onClick={handleCloseDocuments}
          aria-label="Close documents library"
          disabled={documentsBulkDownloading}
        >
          ×
        </button>
        <div className="my-events-present-inner my-events-documents-inner">
          <div className="my-events-present-header">
            <div className="my-events-present-header-title-wrap">
              <h2 className="my-events-present-title">Event Documents</h2>
              <span className="my-events-present-subtitle">Fliers and hour verification forms</span>
            </div>
            <div className="my-events-present-header-controls">
              <label className="my-events-present-date-field">
                <span className="my-events-present-date-label">From</span>
                <input
                  type="date"
                  className="my-events-present-date-input"
                  value={documentsStartDate}
                  onChange={(e) => setDocumentsStartDate(e.target.value)}
                  disabled={documentsBulkDownloading}
                />
              </label>
              <label className="my-events-present-date-field">
                <span className="my-events-present-date-label">To</span>
                <input
                  type="date"
                  className="my-events-present-date-input"
                  value={documentsEndDate}
                  onChange={(e) => setDocumentsEndDate(e.target.value)}
                  disabled={documentsBulkDownloading}
                />
              </label>
              <button
                type="button"
                className="my-events-present-theme-toggle"
                onClick={() => setDocumentsLightBackground((prev) => !prev)}
                aria-label={documentsLightBackground ? 'Switch to dark background' : 'Switch to light background'}
                title={documentsLightBackground ? 'Dark background' : 'Light background'}
                disabled={documentsBulkDownloading}
              >
                <span className="my-events-present-theme-toggle-swatch" aria-hidden="true" />
              </button>
            </div>
          </div>

          {documentsError && (
            <div className="my-events-present-error-banner">{documentsError}</div>
          )}

          <div className="my-events-documents-bulk-bar">
            <p className="my-events-documents-bulk-intro">
              Check event rows for fliers, expand an event and check registrations for hour forms, then choose how to download.
            </p>

            <div className="my-events-documents-bulk-card my-events-documents-bulk-card--featured">
              <div className="my-events-documents-bulk-card-head">
                <span className="my-events-documents-bulk-card-title">Organized archive</span>
                <span className="my-events-documents-bulk-card-badge">Recommended</span>
              </div>
              <p className="my-events-documents-bulk-card-desc">
                One ZIP with a folder per event (date + title). Each folder contains the fliers and verifications you checked above.
              </p>
              <button
                type="button"
                className="my-events-documents-bulk-btn my-events-documents-bulk-btn--primary"
                onClick={handleBulkDownloadDocumentsCombinedZip}
                disabled={documentsBulkDownloading || selectedCombinedCount === 0}
              >
                {documentsBulkDownloading ? 'Building zip…' : `Download ZIP (${selectedCombinedCount} event folder${selectedCombinedCount === 1 ? '' : 's'})`}
              </button>
            </div>

            <div className="my-events-documents-bulk-split">
              <div className="my-events-documents-bulk-card">
                <span className="my-events-documents-bulk-card-title">Fliers only</span>
                <p className="my-events-documents-bulk-card-desc">
                  Event checkboxes only. Use when you need cover images without hour forms.
                </p>
                <div className="my-events-documents-bulk-select-row">
                  <button
                    type="button"
                    className="my-events-documents-bulk-link"
                    onClick={selectAllDocumentsFliers}
                    disabled={documentsBulkDownloading || documentsLoading || flierCount === 0}
                  >
                    Select all ({flierCount})
                  </button>
                  <span className="my-events-documents-bulk-divider" aria-hidden>·</span>
                  <button
                    type="button"
                    className="my-events-documents-bulk-link"
                    onClick={deselectAllDocumentsFliers}
                    disabled={documentsBulkDownloading || selectedFlierCount === 0}
                  >
                    Clear selection
                  </button>
                </div>
                <div className="my-events-documents-bulk-action-row">
                  <button
                    type="button"
                    className="my-events-documents-bulk-btn"
                    onClick={handleBulkDownloadDocumentsFliersZip}
                    disabled={documentsBulkDownloading || selectedFlierCount === 0}
                    title="One ZIP with a folder per selected event, flier only in each"
                  >
                    ZIP archive ({selectedFlierCount})
                  </button>
                  <button
                    type="button"
                    className="my-events-documents-bulk-btn my-events-documents-bulk-btn--ghost"
                    onClick={handleBulkDownloadDocumentsFliers}
                    disabled={documentsBulkDownloading || selectedFlierCount === 0 || fliersSeparateFilesDisabled}
                    title={separateFilesDisabledTitle(selectedFlierCount) || 'Each flier downloads as its own file'}
                  >
                    Separate files ({selectedFlierCount})
                  </button>
                </div>
              </div>

              <div className="my-events-documents-bulk-card">
                <span className="my-events-documents-bulk-card-title">Hour verifications only</span>
                <p className="my-events-documents-bulk-card-desc">
                  Registration checkboxes only. Verified PDFs grouped by event folder.
                </p>
                <div className="my-events-documents-bulk-select-row">
                  <button
                    type="button"
                    className="my-events-documents-bulk-link"
                    onClick={() => selectAllDocumentsVerifications()}
                    disabled={documentsBulkDownloading || documentsLoading || downloadableVerifications.length === 0}
                  >
                    Select all ({downloadableVerifications.length})
                  </button>
                  <span className="my-events-documents-bulk-divider" aria-hidden>·</span>
                  <button
                    type="button"
                    className="my-events-documents-bulk-link"
                    onClick={deselectAllDocumentsVerifications}
                    disabled={documentsBulkDownloading || selectedVerificationCount === 0}
                  >
                    Clear selection
                  </button>
                </div>
                <div className="my-events-documents-bulk-action-row">
                  <button
                    type="button"
                    className="my-events-documents-bulk-btn"
                    onClick={handleBulkDownloadDocumentsVerificationsZip}
                    disabled={documentsBulkDownloading || selectedVerificationCount === 0}
                    title="One ZIP with a folder per event, verification PDFs only"
                  >
                    ZIP archive ({selectedVerificationCount})
                  </button>
                  <button
                    type="button"
                    className="my-events-documents-bulk-btn my-events-documents-bulk-btn--ghost"
                    onClick={handleBulkDownloadDocumentsVerifications}
                    disabled={documentsBulkDownloading || selectedVerificationCount === 0 || verificationsSeparateFilesDisabled}
                    title={separateFilesDisabledTitle(selectedVerificationCount) || 'Each verification PDF downloads as its own file'}
                  >
                    Separate files ({selectedVerificationCount})
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="my-events-present-grid-hint">
            Expand an event for per-student status: verified (downloadable), absent (grey), or not verified yet (red). Each event row also has its own ZIP button for that event&apos;s folder.
          </p>

          <div className="my-events-present-body">
            {documentsLoading ? (
              <div className="my-events-present-loading">
                <LoadingSpinner />
              </div>
            ) : visibleEvents.length === 0 ? (
              <p className="my-events-present-empty">No official events match the selected date range.</p>
            ) : (
              <div className="my-events-documents-list">
                {visibleEvents.map((event) => {
                  const flier = getEventFlierUrl(event);
                  const isExpanded = documentsExpandedEventIds.has(event.id);
                  const registrations = registrationsByEvent[event.id] || [];
                  const eventDownloadableVerifications = getDownloadableVerificationsForEvent(event);
                  const flierSelected = documentsSelectedFlierIds.has(event.id);

                  return (
                    <div key={event.id} className="my-events-documents-event">
                      <div className="my-events-documents-event-header">
                        <input
                          type="checkbox"
                          className="my-events-documents-checkbox"
                          checked={flierSelected}
                          onChange={() => toggleDocumentsFlierSelection(event.id)}
                          disabled={!flier || documentsBulkDownloading}
                          aria-label={`Select flier for ${event.title}`}
                          title={flier ? 'Select flier for bulk download' : 'No flier available'}
                        />
                        <button
                          type="button"
                          className="my-events-documents-expand-btn"
                          onClick={() => toggleDocumentsEventExpanded(event.id)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? `Collapse ${event.title}` : `Expand ${event.title}`}
                          disabled={documentsBulkDownloading}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                        <div className="my-events-documents-event-thumb">
                          {flier ? (
                            <img src={flier} alt="" className="my-events-documents-event-thumb-img" />
                          ) : (
                            <div className="my-events-documents-event-thumb-placeholder">
                              {event.title ? event.title.charAt(0).toUpperCase() : '—'}
                            </div>
                          )}
                        </div>
                        <div className="my-events-documents-event-info">
                          <div className="my-events-documents-event-title" title={event.title}>{event.title}</div>
                          <div className="my-events-documents-event-meta">
                            {event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : 'No date'}
                            {event.type ? ` · ${event.type}` : ''}
                          </div>
                        </div>
                        <div className="my-events-documents-event-actions">
                          <button
                            type="button"
                            className="my-events-documents-download-btn"
                            onClick={() => handleDownloadEventFlier(event)}
                            disabled={!flier || documentsBulkDownloading}
                            title={flier ? 'Download flier' : 'No flier available'}
                            aria-label={flier ? `Download flier for ${event.title}` : `No flier for ${event.title}`}
                          >
                            <DownloadIcon />
                            <span>Flier</span>
                          </button>
                          <button
                            type="button"
                            className="my-events-documents-download-btn my-events-documents-download-btn--zip"
                            onClick={() => handleDownloadEventDocumentsZip(event)}
                            disabled={
                              documentsBulkDownloading
                              || (!flier && eventDownloadableVerifications.length === 0)
                            }
                            title="Download event folder as ZIP (flier + verifications)"
                            aria-label={`Download ZIP folder for ${event.title}`}
                          >
                            <FolderIcon />
                            <span>ZIP</span>
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="my-events-documents-registrations">
                          <div className="my-events-documents-reg-toolbar">
                            <span className="my-events-documents-reg-count">
                              {registrations.length} registration{registrations.length === 1 ? '' : 's'}
                              {eventDownloadableVerifications.length > 0
                                ? ` · ${eventDownloadableVerifications.length} verified`
                                : ''}
                            </span>
                            <button
                              type="button"
                              className="my-events-documents-bulk-link"
                              onClick={() => selectAllDocumentsVerifications([event])}
                              disabled={documentsBulkDownloading || eventDownloadableVerifications.length === 0}
                            >
                              Select all verifications for this event
                            </button>
                          </div>
                          {registrations.length === 0 ? (
                            <p className="my-events-documents-reg-empty">No registrations for this event yet.</p>
                          ) : (
                            <ul className="my-events-documents-reg-list">
                              {registrations.map((registration) => {
                                const rowState = getVerificationRowState(registration);
                                const downloadable = registrationIsDownloadable(registration);
                                const selected = documentsSelectedVerificationKeys.has(
                                  verificationSelectionKey(event.id, registration.id)
                                );
                                const statusLabel = rowState === 'absent'
                                  ? 'Did not attend'
                                  : rowState === 'unverified'
                                    ? 'Hours not verified'
                                    : `Verified${registration.hours != null ? ` (${registration.hours}h)` : ''}`;

                                return (
                                  <li
                                    key={registration.id}
                                    className={[
                                      'my-events-documents-reg-item',
                                      `my-events-documents-reg-item--${rowState}`,
                                    ].join(' ')}
                                  >
                                    <input
                                      type="checkbox"
                                      className="my-events-documents-checkbox"
                                      checked={selected}
                                      onChange={() => toggleDocumentsVerificationSelection(event.id, registration.id)}
                                      disabled={!downloadable || documentsBulkDownloading}
                                      aria-label={`Select verification for ${registration.student_name || 'student'}`}
                                    />
                                    <div className="my-events-documents-reg-main">
                                      <span className="my-events-documents-reg-name">
                                        {registration.student_name || 'Unknown student'}
                                      </span>
                                      <span className="my-events-documents-reg-status">{statusLabel}</span>
                                    </div>
                                    <button
                                      type="button"
                                      className="my-events-documents-download-btn my-events-documents-download-btn--small"
                                      onClick={() => handleDownloadVerification(event, registration)}
                                      disabled={!downloadable || documentsBulkDownloading}
                                      title={downloadable ? 'Download hour verification form' : statusLabel}
                                      aria-label={downloadable
                                        ? `Download verification for ${registration.student_name || 'student'}`
                                        : statusLabel}
                                    >
                                      <DownloadIcon />
                                      <span>PDF</span>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleOpenPresent = () => {
    setHelpOpen(false);
    setFilterMenuOpen(false);
    setRegistrationsPopupEvent(null);
    setBulkDownloadOpen(false);
    setDocumentsOpen(false);
    setPresentError('');
    setPresentStartDate(getEarliestEventDateInput(officialEvents));
    setPresentEndDate('');
    setPresentExpandedEventId(null);
    setPresentLightBackground(false);
    setPresentOpen(true);
  };

  const handleClosePresent = () => {
    if (presentKioskLock) return;
    setPresentOpen(false);
    setPresentExpandedEventId(null);
    setPresentError('');
  };

  const getPresentAnalytics = () => {
    const visibleEvents = getPresentVisibleEvents();
    let totalRegistrations = 0;
    let totalAttended = 0;
    let totalHours = 0;

    const eventCards = visibleEvents.map((event) => {
      const registrations = registrationsByEvent[event.id] || [];
      const stats = computeEventRegistrationStats(registrations);
      totalRegistrations += stats.registrationCount;
      totalAttended += stats.attendedCount;
      totalHours += stats.totalHours;
      return { event, stats };
    });

    return {
      totalEvents: visibleEvents.length,
      totalRegistrations,
      totalAttended,
      totalHours: Math.round(totalHours),
      eventCards,
    };
  };

  const formatStatNumber = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString('en-US');
  };

  const formatHoursStat = (value) => formatStatNumber(Math.round(Number(value) || 0));

  const navigatePresentDetail = (direction) => {
    const { eventCards } = getPresentAnalytics();
    if (eventCards.length <= 1 || !presentExpandedEventId) return;

    const currentIndex = eventCards.findIndex(({ event }) => event.id === presentExpandedEventId);
    if (currentIndex === -1) return;

    const delta = direction === 'next' ? 1 : -1;
    const nextIndex = (currentIndex + delta + eventCards.length) % eventCards.length;
    setPresentExpandedEventId(eventCards[nextIndex].event.id);
  };

  const renderPresentOverlay = () => {
    if (!presentOpen) return null;

    const analytics = getPresentAnalytics();
    const heroDisplay = (value) => (presentLoading ? '…' : formatStatNumber(value));
    const heroHoursDisplay = (value) => (presentLoading ? '…' : formatHoursStat(value));
    const expandedCard = presentExpandedEventId
      ? analytics.eventCards.find(({ event }) => event.id === presentExpandedEventId)
      : null;

    return (
      <div
        className={`my-events-present-layer ${presentLightBackground ? 'my-events-present-layer--light' : 'my-events-present-layer--dark'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Field Experience analytics"
      >
        {!presentKioskLock && (
          <button
            type="button"
            className="my-events-present-close"
            onClick={handleClosePresent}
            aria-label="Close analytics"
          >
            ×
          </button>
        )}
        <div className="my-events-present-inner">
          <div className="my-events-present-header">
            <div className="my-events-present-header-title-wrap">
              <h2 className="my-events-present-title">Field Experience Events</h2>
              <span className="my-events-present-subtitle">Official events</span>
            </div>
            <div className="my-events-present-header-controls">
              <label className="my-events-present-date-field">
                <span className="my-events-present-date-label">From</span>
                <input
                  type="date"
                  className="my-events-present-date-input"
                  value={presentStartDate}
                  onChange={(e) => setPresentStartDate(e.target.value)}
                />
              </label>
              <label className="my-events-present-date-field">
                <span className="my-events-present-date-label">To</span>
                <input
                  type="date"
                  className="my-events-present-date-input"
                  value={presentEndDate}
                  onChange={(e) => setPresentEndDate(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="my-events-present-theme-toggle"
                onClick={() => setPresentLightBackground((prev) => !prev)}
                aria-label={presentLightBackground ? 'Switch to dark background' : 'Switch to light background'}
                title={presentLightBackground ? 'Dark background' : 'Light background'}
              >
                <span className="my-events-present-theme-toggle-swatch" aria-hidden="true" />
              </button>
            </div>
          </div>

          {presentError && (
            <div className="my-events-present-error-banner">{presentError}</div>
          )}

          <div className="my-events-present-hero-row">
            <div className="my-events-present-hero-card my-events-present-hero-card--events">
              <span className="my-events-present-hero-value">{formatStatNumber(analytics.totalEvents)}</span>
              <span className="my-events-present-hero-label">Events</span>
            </div>
            <div className="my-events-present-hero-card my-events-present-hero-card--registrations">
              <span className="my-events-present-hero-value">{heroDisplay(analytics.totalRegistrations)}</span>
              <span className="my-events-present-hero-label">Registrations</span>
            </div>
            <div className="my-events-present-hero-card my-events-present-hero-card--hours">
              <span className="my-events-present-hero-value">{heroHoursDisplay(analytics.totalHours)}</span>
              <span className="my-events-present-hero-label">CTE Hours Awarded</span>
            </div>
            <div className="my-events-present-hero-card my-events-present-hero-card--attended">
              <span className="my-events-present-hero-value">{heroDisplay(analytics.totalAttended)}</span>
              <span className="my-events-present-hero-label">Students attended</span>
            </div>
          </div>

          {!presentLoading && analytics.eventCards.length > 0 && (
            <p className="my-events-present-grid-hint">
              {presentViewMode === 'grid'
                ? 'Click an event to open a full-screen preview, or switch to list view to scroll through fliers.'
                : 'Scroll through each event at full size. Switch to grid view for a compact overview.'}
            </p>
          )}

          <div className="my-events-present-body">
            {presentLoading ? (
              <div className="my-events-present-loading">
                <LoadingSpinner />
              </div>
            ) : analytics.eventCards.length === 0 ? (
              <p className="my-events-present-empty">No official events match the selected date range.</p>
            ) : presentViewMode === 'list' ? (
              <div className="my-events-present-list">
                {analytics.eventCards.map(({ event, stats }) => {
                  const flier = event.coverPhoto || event.coverphoto || '';
                  return (
                    <div
                      key={event.id}
                      className="my-events-present-list-item my-events-present-list-item--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setPresentExpandedEventId(event.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setPresentExpandedEventId(event.id);
                        }
                      }}
                      aria-label={`View details for ${event.title}`}
                    >
                      <div className="my-events-present-list-flier">
                        {flier ? (
                          <img src={flier} alt={event.title} className="my-events-present-list-flier-img" />
                        ) : (
                          <div className="my-events-present-list-flier-placeholder">
                            {event.title ? event.title.charAt(0).toUpperCase() : 'F'}
                          </div>
                        )}
                        <span className="my-events-present-flier-badge">
                          {formatStatNumber(stats.registrationCount)} registered
                        </span>
                      </div>
                      <div className="my-events-present-list-panel">
                        <div className="my-events-present-list-title" title={event.title}>{event.title}</div>
                        <div className="my-events-present-list-meta">
                          {event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : 'No date'}
                          {event.type ? ` · ${event.type}` : ''}
                          {event.status ? ` · ${event.status}` : ''}
                        </div>
                        <div className="my-events-present-list-stats">
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatStatNumber(stats.registrationCount)}</span>
                            <span className="my-events-present-card-stat-label">Registrations</span>
                          </div>
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatStatNumber(stats.attendedCount)}</span>
                            <span className="my-events-present-card-stat-label">Attended</span>
                          </div>
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatHoursStat(stats.totalHours)}</span>
                            <span className="my-events-present-card-stat-label">CTE Hours Awarded</span>
                          </div>
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatHoursStat(stats.averageHours)}</span>
                            <span className="my-events-present-card-stat-label">Avg hours / student</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="my-events-present-grid">
                {analytics.eventCards.map(({ event, stats }) => {
                  const flier = event.coverPhoto || event.coverphoto || '';
                  return (
                    <div
                      key={event.id}
                      className="my-events-present-card my-events-present-card--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setPresentExpandedEventId(event.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setPresentExpandedEventId(event.id);
                        }
                      }}
                      aria-label={`View details for ${event.title}`}
                    >
                      <div className="my-events-present-flier">
                        {flier ? (
                          <img src={flier} alt={event.title} className="my-events-present-flier-img" />
                        ) : (
                          <div className="my-events-present-flier-placeholder">
                            {event.title ? event.title.charAt(0).toUpperCase() : 'F'}
                          </div>
                        )}
                        <span className="my-events-present-flier-badge">
                          {formatStatNumber(stats.registrationCount)} registered
                        </span>
                      </div>
                      <div className="my-events-present-card-body">
                        <div className="my-events-present-card-title" title={event.title}>{event.title}</div>
                        <div className="my-events-present-card-date">
                          {event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : 'No date'}
                        </div>
                        <div className="my-events-present-card-stats">
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatStatNumber(stats.attendedCount)}</span>
                            <span className="my-events-present-card-stat-label">Attended</span>
                          </div>
                          <div className="my-events-present-card-stat">
                            <span className="my-events-present-card-stat-value">{formatHoursStat(stats.totalHours)}</span>
                            <span className="my-events-present-card-stat-label">CTE Hours Awarded</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {!presentLoading && analytics.eventCards.length > 0 && (
          <div className="my-events-present-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`my-events-present-view-btn ${presentViewMode === 'grid' ? 'my-events-present-view-btn--active' : ''}`}
              onClick={() => setPresentViewMode('grid')}
              aria-pressed={presentViewMode === 'grid'}
              aria-label="Grid view"
              title="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <rect x="3" y="3" width="8" height="8" rx="1.5" />
                <rect x="13" y="3" width="8" height="8" rx="1.5" />
                <rect x="3" y="13" width="8" height="8" rx="1.5" />
                <rect x="13" y="13" width="8" height="8" rx="1.5" />
              </svg>
              <span className="my-events-present-view-btn-label">Grid</span>
            </button>
            <button
              type="button"
              className={`my-events-present-view-btn ${presentViewMode === 'list' ? 'my-events-present-view-btn--active' : ''}`}
              onClick={() => setPresentViewMode('list')}
              aria-pressed={presentViewMode === 'list'}
              aria-label="List view"
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <rect x="3" y="4" width="18" height="4" rx="1.5" />
                <rect x="3" y="10" width="18" height="4" rx="1.5" />
                <rect x="3" y="16" width="18" height="4" rx="1.5" />
              </svg>
              <span className="my-events-present-view-btn-label">List</span>
            </button>
          </div>
        )}

        {expandedCard && (() => {
          const { event, stats } = expandedCard;
          const flier = event.coverPhoto || event.coverphoto || '';
          const expandedIndex = analytics.eventCards.findIndex(({ event: e }) => e.id === event.id);
          const showNav = analytics.eventCards.length > 1;

          return (
            <div
              className="my-events-present-detail-layer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="my-events-present-detail-title"
              onClick={() => setPresentExpandedEventId(null)}
            >
              <button
                type="button"
                className="my-events-present-detail-close"
                onClick={() => setPresentExpandedEventId(null)}
                aria-label="Close event details"
              >
                ×
              </button>
              {showNav && (
                <>
                  <button
                    type="button"
                    className="my-events-present-detail-nav my-events-present-detail-nav--prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePresentDetail('prev');
                    }}
                    aria-label="Previous event"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="my-events-present-detail-nav my-events-present-detail-nav--next"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigatePresentDetail('next');
                    }}
                    aria-label="Next event"
                  >
                    ›
                  </button>
                </>
              )}
              <div
                className="my-events-present-detail-stage"
                onClick={(e) => e.stopPropagation()}
              >
                {flier ? (
                  <img
                    src={flier}
                    alt={event.title}
                    className="my-events-present-detail-flier-img"
                  />
                ) : (
                  <div className="my-events-present-detail-flier-placeholder">
                    {event.title ? event.title.charAt(0).toUpperCase() : 'F'}
                  </div>
                )}
              </div>
              <div
                className="my-events-present-detail-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="my-events-present-detail-panel-header">
                  <h3 id="my-events-present-detail-title" className="my-events-present-detail-title">
                    {event.title}
                  </h3>
                  {showNav && (
                    <span className="my-events-present-detail-counter">
                      {expandedIndex + 1} / {analytics.eventCards.length}
                    </span>
                  )}
                </div>
                <div className="my-events-present-detail-meta">
                  {event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : 'No date'}
                  {event.type ? ` · ${event.type}` : ''}
                  {event.status ? ` · ${event.status}` : ''}
                </div>
                <div className="my-events-present-detail-stats">
                  <div className="my-events-present-detail-stat">
                    <span className="my-events-present-detail-stat-value">{formatStatNumber(stats.registrationCount)}</span>
                    <span className="my-events-present-detail-stat-label">Registrations</span>
                  </div>
                  <div className="my-events-present-detail-stat">
                    <span className="my-events-present-detail-stat-value">{formatStatNumber(stats.attendedCount)}</span>
                    <span className="my-events-present-detail-stat-label">Attended</span>
                  </div>
                  <div className="my-events-present-detail-stat">
                    <span className="my-events-present-detail-stat-value">{formatHoursStat(stats.totalHours)}</span>
                    <span className="my-events-present-detail-stat-label">CTE Hours Awarded</span>
                  </div>
                  <div className="my-events-present-detail-stat">
                    <span className="my-events-present-detail-stat-value">{formatHoursStat(stats.averageHours)}</span>
                    <span className="my-events-present-detail-stat-label">Avg hours / student</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const handleOpenRegistrations = async (event, e) => {
    e.stopPropagation();
    setHelpOpen(false);
    setFilterMenuOpen(false);
    setBulkDownloadOpen(false);
    setDocumentsOpen(false);
    setRegistrationsPopupEvent(event);
    setRegistrationsError('');
    if (!registrationsByEvent[event.id]) {
      await fetchEventRegistrations(event.id);
    }
  };

  const handleCloseRegistrations = () => {
    setRegistrationsPopupEvent(null);
    setRegistrationsError('');
  };

  const renderRegistrationsPopup = () => {
    if (!registrationsPopupEvent) return null;

    const event = registrationsPopupEvent;
    const registrations = registrationsByEvent[event.id];
    const isLoading = loadingRegistrationsEventId === event.id;
    const popupTitleId = 'my-events-registrations-title';
    const canDownload = Array.isArray(registrations) && registrations.length > 0 && !isLoading;

    return (
      <div className="my-events-help-layer" role="presentation">
        <button
          type="button"
          className="my-events-help-backdrop"
          aria-label="Close registrations"
          onClick={handleCloseRegistrations}
        />
        <div
          className="my-events-help-popup my-events-registrations-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby={popupTitleId}
        >
          <div style={styles.helpPopupHeader}>
            <h2 id={popupTitleId} style={styles.helpPopupTitle}>
              Registrations
              {!isLoading && Array.isArray(registrations) && (
                <span style={styles.registrationsPopupCount}> ({registrations.length})</span>
              )}
            </h2>
            <div style={styles.registrationsPopupHeaderActions}>
              {canDownload && (
                <button
                  type="button"
                  className="my-events-registrations-download-btn"
                  style={styles.registrationsDownloadButton}
                  onClick={() => handleDownloadRegistrationsCsv(event, registrations)}
                  title="Download CSV"
                  aria-label="Download registrations CSV"
                >
                  <DownloadIcon />
                  <span>CSV</span>
                </button>
              )}
              <button
                type="button"
                className="my-events-help-close"
                style={styles.helpPopupClose}
                onClick={handleCloseRegistrations}
                aria-label="Close registrations"
              >
                ×
              </button>
            </div>
          </div>
          <div style={styles.registrationsPopupEventMeta}>
            {event.title}
            {event.date ? ` · ${dateTimeToMonDDYYYYWithTime(event.date, false)}` : ''}
          </div>
          <div style={styles.helpPopupBody}>
            {registrationsError && (
              <div style={styles.registrationsPopupError}>{registrationsError}</div>
            )}
            {isLoading && (
              <div style={styles.registrationsPopupLoading}>
                <LoadingSpinner />
              </div>
            )}
            {!isLoading && !registrationsError && Array.isArray(registrations) && registrations.length === 0 && (
              <p style={styles.registrationsPopupEmpty}>No registrations for this event yet.</p>
            )}
            {!isLoading && !registrationsError && Array.isArray(registrations) && registrations.length > 0 && (
              <div className="my-events-registrations-table-wrap">
                <table className="my-events-registrations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Grade</th>
                      <th>Attended</th>
                      <th>Hours verified</th>
                      <th>Verification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map(reg => (
                      <tr key={reg.id}>
                        <td>{reg.student_name || 'Unknown'}</td>
                        <td>{reg.gradelevel || '—'}</td>
                        <td>{getRegistrationAttendanceLabel(reg)}</td>
                        <td>{getRegistrationHoursVerifiedLabel(reg)}</td>
                        <td>
                          {reg.pdflink ? (
                            <a
                              href={reg.pdflink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="my-events-registrations-link"
                              title="Open hour verification PDF"
                              onClick={(e) => e.stopPropagation()}
                            >
                              PDF
                            </a>
                          ) : (
                            <span style={styles.registrationsNoLink}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBulkDownloadPopup = () => {
    if (!bulkDownloadOpen) return null;

    const visibleEvents = getBulkDownloadVisibleEvents();
    const selectedVisibleCount = visibleEvents.filter((event) => bulkSelectedEventIds.has(event.id)).length;
    const popupTitleId = 'my-events-bulk-download-title';

    return (
      <div className="my-events-help-layer" role="presentation">
        <button
          type="button"
          className="my-events-help-backdrop"
          aria-label="Close download registrations"
          onClick={handleCloseBulkDownload}
          disabled={bulkDownloadLoading}
        />
        <div
          className="my-events-help-popup my-events-bulk-download-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby={popupTitleId}
        >
          <div style={styles.helpPopupHeader}>
            <h2 id={popupTitleId} style={styles.helpPopupTitle}>Download</h2>
            <button
              type="button"
              className="my-events-help-close"
              style={styles.helpPopupClose}
              onClick={handleCloseBulkDownload}
              aria-label="Close download"
              disabled={bulkDownloadLoading}
            >
              ×
            </button>
          </div>
          <div style={styles.helpPopupBody}>
            <div className="my-events-bulk-download-tabs" role="tablist" aria-label="Download type">
              <button
                type="button"
                role="tab"
                aria-selected={bulkDownloadTab === 'events'}
                className={[
                  'my-events-bulk-download-tab',
                  bulkDownloadTab === 'events' && 'my-events-bulk-download-tab--active',
                ].filter(Boolean).join(' ')}
                style={{
                  ...styles.bulkDownloadTab,
                  ...(bulkDownloadTab === 'events' ? styles.bulkDownloadTabActive : {}),
                }}
                onClick={() => {
                  setBulkDownloadTab('events');
                  setBulkDownloadError('');
                }}
                disabled={bulkDownloadLoading}
              >
                Event data
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={bulkDownloadTab === 'registrations'}
                className={[
                  'my-events-bulk-download-tab',
                  bulkDownloadTab === 'registrations' && 'my-events-bulk-download-tab--active',
                ].filter(Boolean).join(' ')}
                style={{
                  ...styles.bulkDownloadTab,
                  ...(bulkDownloadTab === 'registrations' ? styles.bulkDownloadTabActive : {}),
                }}
                onClick={() => {
                  setBulkDownloadTab('registrations');
                  setBulkDownloadError('');
                }}
                disabled={bulkDownloadLoading}
              >
                Registrations
              </button>
            </div>
            <p style={styles.bulkDownloadIntro}>
              {bulkDownloadTab === 'events'
                ? 'Choose events to include. Registration stats are fetched when you download.'
                : 'Choose events to include. Student registrations are fetched when you download.'}
            </p>
            <div style={styles.bulkDownloadDateRow}>
              <label style={styles.bulkDownloadDateField}>
                <span style={styles.bulkDownloadDateLabel}>Stage</span>
                <select
                  value={bulkDownloadStageFilter}
                  onChange={(e) => setBulkDownloadStageFilter(e.target.value)}
                  style={styles.bulkDownloadDateInput}
                  disabled={bulkDownloadLoading}
                >
                  <option value="official">Official</option>
                  <option value="all">All stages</option>
                  <option value="request">Request</option>
                  <option value="saf">SAF</option>
                </select>
              </label>
              <label style={styles.bulkDownloadDateField}>
                <span style={styles.bulkDownloadDateLabel}>Start date</span>
                <input
                  type="date"
                  value={bulkDownloadStartDate}
                  onChange={(e) => setBulkDownloadStartDate(e.target.value)}
                  style={styles.bulkDownloadDateInput}
                  disabled={bulkDownloadLoading}
                />
              </label>
              <label style={styles.bulkDownloadDateField}>
                <span style={styles.bulkDownloadDateLabel}>End date</span>
                <input
                  type="date"
                  value={bulkDownloadEndDate}
                  onChange={(e) => setBulkDownloadEndDate(e.target.value)}
                  style={styles.bulkDownloadDateInput}
                  disabled={bulkDownloadLoading}
                />
              </label>
            </div>
            <div style={styles.bulkDownloadActionsRow}>
              <span style={styles.bulkDownloadSelectionMeta}>
                {selectedVisibleCount} of {visibleEvents.length} shown selected
              </span>
              <div style={styles.bulkDownloadActionButtons}>
                <button
                  type="button"
                  style={styles.bulkDownloadLinkButton}
                  onClick={handleBulkSelectAllVisible}
                  disabled={bulkDownloadLoading || visibleEvents.length === 0}
                >
                  Select all
                </button>
                <button
                  type="button"
                  style={styles.bulkDownloadLinkButton}
                  onClick={handleBulkDeselectAllVisible}
                  disabled={bulkDownloadLoading || visibleEvents.length === 0}
                >
                  Deselect all
                </button>
              </div>
            </div>
            {bulkDownloadError && (
              <div style={styles.registrationsPopupError}>{bulkDownloadError}</div>
            )}
            <div className="my-events-bulk-download-list">
              {visibleEvents.length === 0 ? (
                <p style={styles.registrationsPopupEmpty}>No events match the selected date range.</p>
              ) : (
                visibleEvents.map((event) => {
                  const checked = bulkSelectedEventIds.has(event.id);
                  return (
                    <label key={event.id} style={styles.bulkDownloadEventItem}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBulkDownloadEvent(event.id)}
                        disabled={bulkDownloadLoading}
                      />
                      <span style={styles.bulkDownloadEventText}>
                        <span style={styles.bulkDownloadEventTitle}>{event.title}</span>
                        <span style={styles.bulkDownloadEventMeta}>
                          {event.date ? dateTimeToMonDDYYYYWithTime(event.date, false) : 'No date'}
                          {event.type ? ` · ${event.type}` : ''}
                          {event.stage ? ` · ${event.stage}` : ''}
                        </span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div style={styles.bulkDownloadFooter}>
              <button
                type="button"
                style={styles.bulkDownloadCancelButton}
                onClick={handleCloseBulkDownload}
                disabled={bulkDownloadLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.bulkDownloadConfirmButton}
                onClick={handleBulkDownloadConfirm}
                disabled={bulkDownloadLoading || selectedVisibleCount === 0}
              >
                {bulkDownloadLoading ? 'Downloading…' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hasCoordinatorPendingActions = (event) => {
    const inv = event.coordinatorInvitation;
    if (!inv || !canShowCoordinatorActions(inv)) return false;
    const cs = getCoordinatorStatus(inv);
    return (
      (cs.involved === null && cs.requestdecision === null) ||
      cs.involved === false ||
      (shouldShowDecisionButtons(inv) && event.stage === 'request') ||
      (canForceApprove(inv) && event.stage === 'request') ||
      (event.status === 'approved' && event.stage === 'request') ||
      (cs.involved && event.stage === 'request')
    );
  };

  const hasInvitationsPanel = (event) => {
    if (event.trainerInvitations.length > 0) return true;
    if (hasCoordinatorPendingActions(event)) return true;
    if (hasCoordinatorSafInvitationNote(event)) return true;
    return false;
  };

  const handleTrainerInvolvement = async (tpId, involved) => {
    try {
      setActionLoading(prev => ({ ...prev, [`trainer-involvement-${tpId}-${involved}`]: true }));
      const token = localStorage.getItem('token');
      await axios.patch(`/api/field-experience-events/training-invitations/${tpId}/involvement`,
        { involved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadTrainerInvitations();
      
      // Set the last action event ID and scroll to the card
      const eventId = trainerInvitations.find(tp => tp.id === tpId)?.feevent_id;
      if (eventId) {
        setLastActionEventId(eventId);
        setHasInitialScroll(true); // Prevent initial scroll from interfering
        setTimeout(() => {
          if (eventRefs.current[eventId]) {
            eventRefs.current[eventId].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
        
        // Clear the last action indicator after 5 seconds
        setTimeout(() => {
          setLastActionEventId(null);
        }, 5000);
      }
    } catch (error) {
      setError('Failed to update trainer involvement');
      await loadTrainerInvitations();
    } finally {
      setActionLoading(prev => ({ ...prev, [`trainer-involvement-${tpId}-${involved}`]: false }));
    }
  };

  const renderInvitationsPanel = (event) => {
    const multipleAcceptedTrainingPlans = hasMultipleAcceptedTrainerInvitations(event);

    return (
    <div className="my-events-invitations-panel" style={styles.invitationsPanel}>
      {hasCoordinatorSafInvitationNote(event) && (
        <div className="my-events-invitation-card">
          <div style={styles.actionRow}>
            <div style={styles.actionRowLeft}>
              <span style={styles.pillBadge}>Coordinator</span>
              <span style={styles.actionPrompt}>
                Your coordinator invitation
                <span style={styles.actionStatus}> — accepted</span>
                <span style={styles.actionSubtext}>
                  Open the schedule approval form using the{' '}
                  <span style={styles.inlineActionIcon}><OpenInNewTabIcon /></span>
                  {' '}in the row.
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {event.coordinatorInvitation && hasCoordinatorPendingActions(event) && (() => {
        const coordinatorStatus = getCoordinatorStatus(event.coordinatorInvitation);
        return (
          <div className="my-events-invitation-card">
            {coordinatorStatus.involved === null && coordinatorStatus.requestdecision === null && (
              <div style={styles.actionRow}>
                <div style={styles.actionRowLeft}>
                  <span style={styles.pillBadge}>Coordinator</span>
                  <span style={styles.actionPrompt}>Review this event?</span>
                </div>
                <div style={styles.buttonGroup}>
                  <button
                    onClick={() => handleInvolvement(event.coordinatorInvitation.feevent_id, true)}
                    disabled={isAnyInvolvementButtonLoading(event.coordinatorInvitation.feevent_id)}
                    style={{ ...styles.button, ...styles.acceptButton }}
                  >
                    {isInvolvementButtonLoading(event.coordinatorInvitation.feevent_id, true) ? 'Accepting…' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleInvolvement(event.coordinatorInvitation.feevent_id, false)}
                    disabled={isAnyInvolvementButtonLoading(event.coordinatorInvitation.feevent_id)}
                    style={{ ...styles.button, ...styles.declineButton }}
                  >
                    {isInvolvementButtonLoading(event.coordinatorInvitation.feevent_id, false) ? 'Declining…' : 'Decline'}
                  </button>
                </div>
              </div>
            )}

            {coordinatorStatus.involved === false && (
              <div style={styles.actionRow}>
                <div style={styles.actionRowLeft}>
                  <span style={styles.pillBadge}>Coordinator</span>
                  <span style={styles.actionPrompt}>You declined involvement.</span>
                </div>
                <div style={styles.buttonGroup}>
                  <button
                    onClick={() => handleInvolvement(event.coordinatorInvitation.feevent_id, true)}
                    disabled={isAnyInvolvementButtonLoading(event.coordinatorInvitation.feevent_id)}
                    style={{ ...styles.button, ...styles.acceptButton }}
                  >
                    {isInvolvementButtonLoading(event.coordinatorInvitation.feevent_id, true) ? 'Accepting…' : 'Accept'}
                  </button>
                  <button
                    disabled={true}
                    style={{ ...styles.button, ...styles.declineButton, ...styles.disabledButton }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            )}

            {shouldShowDecisionButtons(event.coordinatorInvitation) && event.stage === 'request' && (
              <div style={styles.actionRow}>
                <div style={styles.actionRowLeft}>
                  <span style={styles.pillBadge}>Coordinator</span>
                  <span style={styles.actionPrompt}>
                    {canMakeDecision(event.coordinatorInvitation)
                      ? `Your decision on this ${event.stage}:`
                      : 'Change your decision:'
                    }
                  </span>
                </div>
                <div style={styles.buttonGroup}>
                  {(() => {
                    const isRequestStage = event.stage === 'request';
                    const currentDecision = isRequestStage ? coordinatorStatus.requestdecision : coordinatorStatus.safdecision;
                    return (
                      <>
                        <button
                          onClick={() => handleDecision(event.coordinatorInvitation.feevent_id, 'approved')}
                          disabled={isAnyButtonLoading(event.coordinatorInvitation.feevent_id) || currentDecision === 'approved'}
                          style={{
                            ...styles.button,
                            ...styles.approveButton,
                            ...(currentDecision === 'approved' && styles.disabledButton)
                          }}
                        >
                          {isButtonLoading(event.coordinatorInvitation.feevent_id, 'approved') ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleDecision(event.coordinatorInvitation.feevent_id, 'denied')}
                          disabled={isAnyButtonLoading(event.coordinatorInvitation.feevent_id) || currentDecision === 'denied'}
                          style={{
                            ...styles.button,
                            ...styles.denyButton,
                            ...(currentDecision === 'denied' && styles.disabledButton)
                          }}
                        >
                          {isButtonLoading(event.coordinatorInvitation.feevent_id, 'denied') ? 'Denying…' : 'Deny'}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {canForceApprove(event.coordinatorInvitation) && event.stage === 'request' && (
              <div style={{ ...styles.actionRow, ...styles.noticeRow }}>
                <span style={styles.actionPrompt}>
                  1 of 2 approvals made — force-approve to approve this stage now.
                </span>
                <button
                  onClick={() => handleDecision(event.coordinatorInvitation.feevent_id, 'approved', true)}
                  disabled={isAnyButtonLoading(event.coordinatorInvitation.feevent_id)}
                  style={{ ...styles.button, ...styles.forceApproveButton }}
                >
                  {isButtonLoading(event.coordinatorInvitation.feevent_id, 'force-approve') ? 'Force Approving…' : 'Force Approve'}
                </button>
              </div>
            )}

            {event.status === 'approved' && event.stage === 'request' && (
              <div style={{ ...styles.actionRow, ...styles.noticeRow }}>
                <span style={styles.actionPrompt}>Request approved — Lead Instructor must now fill out the SAF.</span>
              </div>
            )}

            {getCoordinatorStatus(event.coordinatorInvitation).involved && event.stage === 'request' && (
              <div style={styles.decisionHistory}>
                <h4 style={styles.sectionHeading}>Coordinator Decisions</h4>
                {event.coordinatorInvitation.allCoordinatorDecisions && event.coordinatorInvitation.allCoordinatorDecisions.length > 0 ? (
                  event.coordinatorInvitation.allCoordinatorDecisions.map((coordinator, index) => {
                    const isRequestStage = event.stage === 'request';
                    const decision = isRequestStage ? coordinator.requestdecision : coordinator.safdecision;
                    const decisionHistory = isRequestStage ? coordinator.requestdecisionhistory : coordinator.safdecisionhistory;
                    const stageLabel = isRequestStage ? 'REQUEST' : 'SAF';

                    return (
                      <div key={index} style={styles.historyEntry}>
                        <div style={styles.historyInfo}>
                          <span style={styles.historyAction}>{decision || 'No decision yet'}</span>
                          <span style={styles.historyStage}> {stageLabel}</span>
                          <span style={styles.historyCoordinator}>
                            {coordinator.first_name} {coordinator.last_name}
                          </span>
                        </div>
                        <span style={styles.historyTimestamp}>
                          {decision && decisionHistory && decisionHistory.length > 0
                            ? dateTimeToMonDDYYYYWithTime(decisionHistory[decisionHistory.length - 1].timestamp)
                            : ''
                          }
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p style={styles.messageText}>No coordinators are involved yet.</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {event.trainerInvitations.map(tp => {
        const isLoading = actionLoading[`trainer-involvement-${tp.id}-true`] || actionLoading[`trainer-involvement-${tp.id}-false`];
        return (
          <div key={tp.id} className="my-events-invitation-card">
            <div style={styles.actionRow}>
              <div style={styles.actionRowLeft}>
                <span style={styles.pillBadge}>Trainer</span>
                <span style={styles.actionPrompt}>
                  {tp.concentration_title}
                  {tp.involved !== null && (
                    <span style={styles.actionStatus}> — {tp.involved ? 'accepted' : 'declined'}</span>
                  )}
                  {tp.involved !== false && (
                    <span style={styles.actionSubtext}>
                      {tp.involved === true ? (
                        multipleAcceptedTrainingPlans ? (
                          <>Use the{' '}
                            <span style={styles.inlineActionIcon}><EditIcon /></span>
                            {' '}to open this training plan.</>
                        ) : (
                          <>
                            Open the training plan using the{' '}
                            <span style={styles.inlineActionIcon}><HammerIcon /></span>
                            {' '}in the row.
                          </>
                        )
                      ) : (
                        multipleAcceptedTrainingPlans ? (
                          <>
                            After accepting, use the{' '}
                            <span style={styles.inlineActionIcon}><EditIcon /></span>
                            {' '}to open this training plan.
                          </>
                        ) : (
                          <>
                            After accepting, open the training plan using the{' '}
                            <span style={styles.inlineActionIcon}><HammerIcon /></span>
                            {' '}in the row.
                          </>
                        )
                      )}
                    </span>
                  )}
                </span>
              </div>
              <div style={styles.buttonGroup}>
                {tp.involved === true && multipleAcceptedTrainingPlans && (
                  <a
                    href={getTrainingPlanHref(event.id, tp.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="my-events-open-tab-link"
                    title={`Open training plan for ${tp.concentration_title} in new tab`}
                    aria-label={`Open training plan for ${tp.concentration_title} in new tab`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EditIcon />
                  </a>
                )}
                <button
                  onClick={() => handleTrainerInvolvement(tp.id, true)}
                  disabled={isLoading || tp.involved === true}
                  style={{ ...styles.button, ...styles.acceptButton, ...(tp.involved === true ? styles.disabledButton : {}) }}
                >
                  {actionLoading[`trainer-involvement-${tp.id}-true`] ? 'Accepting…' : 'Accept'}
                </button>
                <button
                  onClick={() => handleTrainerInvolvement(tp.id, false)}
                  disabled={isLoading || tp.involved === false}
                  style={{ ...styles.button, ...styles.declineButton, ...(tp.involved === false ? styles.disabledButton : {}) }}
                >
                  {actionLoading[`trainer-involvement-${tp.id}-false`] ? 'Declining…' : 'Decline'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
    );
  };

  const renderHelpLegendContent = () => (
    <>
      <div style={styles.legendGroup}>
        <div style={styles.legendGroupTitle}>Roles</div>
        <div style={styles.roleLegendItem}>
          <span style={styles.pillBadge}>Coordinator</span>
          <span style={styles.roleLegendText}>
            Reviews jobs and training plans and finalizes the event as official.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.pillBadge}>Trainer</span>
          <span style={styles.roleLegendText}>
            Proposes jobs and training plan submission for the event.
          </span>
        </div>
      </div>

      <div style={styles.legendGroup}>
        <div style={styles.legendGroupTitle}>Actions</div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><OpenInNewTabIcon /></span>
          <span style={styles.roleLegendText}>
            Open the schedule approval form in a new tab when you have access.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><HammerIcon /></span>
          <span style={styles.roleLegendText}>
            Open a training plan directly, or open invitations to choose when you have multiple accepted concentrations.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><EditIcon /></span>
          <span style={styles.roleLegendText}>
            Open a specific training plan in a new tab from the invitations panel (left of Accept/Decline) when multiple concentrations are accepted.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><EnvelopeIcon /></span>
          <span style={styles.roleLegendText}>
            Click a row to open details and invitations, or use the envelope icon to toggle the invitations panel. A number on the envelope shows how many invitations still need your response.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><ListIcon /></span>
          <span style={styles.roleLegendText}>
            Open a slide-up list of student registrations for the event (name, grade, attendance, hours verified, and verification PDF when available).
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><DownloadIcon /></span>
          <span style={styles.roleLegendText}>
            Download event summaries or student registrations as CSV. Filter by stage and date, select events, then confirm to fetch and export. Also opens Present analytics mode.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={styles.iconButtonLegend} aria-hidden><FolderIcon /></span>
          <span style={styles.roleLegendText}>
            Open the documents library in full screen. Download event fliers and hour verification PDFs individually, as separate files, or as organized ZIP archives with one folder per event (date + title).
          </span>
        </div>
      </div>

      <div style={styles.legendGroup}>
        <div style={styles.legendGroupTitle}>Stages</div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getStageBadgeStyle('request') }}>Request</span>
          <span style={styles.roleLegendText}>
            Initial stage needing approval before entering more information in SAF.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getStageBadgeStyle('saf') }}>SAF</span>
          <span style={styles.roleLegendText}>
            Schedule Approval Form — all event details are gathered and trainers submit jobs and training plans.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getStageBadgeStyle('official') }}>Official</span>
          <span style={styles.roleLegendText}>
            Event can appear on the calendar; student registration depends on status below.
          </span>
        </div>
      </div>

      <div style={styles.legendGroup}>
        <div style={styles.legendGroupTitle}>Official registration status</div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getOfficialStatusBadgeStyle('hidden') }}>Hidden</span>
          <span style={styles.roleLegendText}>
            Does not appear on the calendar or registration portal.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getOfficialStatusBadgeStyle('locked') }}>Locked</span>
          <span style={styles.roleLegendText}>
            Shows on the calendar but prevents student registration.
          </span>
        </div>
        <div style={styles.roleLegendItem}>
          <span style={{ ...styles.pillBadge, ...getOfficialStatusBadgeStyle('live') }}>Live</span>
          <span style={styles.roleLegendText}>
            Shows on the calendar and allows student registration.
          </span>
        </div>
      </div>
    </>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (presentKioskLock) {
    if (loading || !permissionsLoaded) {
      return <LoadingSpinner />;
    }
    if (!canDownloadRegistrations) {
      return (
        <div className="my-events-present-kiosk-denied">
          Present mode is not available for this account.
        </div>
      );
    }
    return renderPresentOverlay() || <LoadingSpinner />;
  }

  return (
    <div
      style={styles.container}
      className={(presentOpen || documentsOpen) ? 'my-events-page--present-open' : undefined}
    >
      <h1 style={styles.title}>Field Experience Events</h1>

      {canViewAllEvents && (
        <div style={styles.topBanner}>
          {hasGlobalAccess()
            ? 'Expanded view — all events visible (View all events access).'
            : 'Expanded view — events for your school are visible (View all events access).'}
        </div>
      )}

      {filteredEventId && (
        <div style={styles.topBanner}>
          <span>Showing filtered event</span>
          <button onClick={clearFilter} style={styles.clearFilterButton}>
            Show All Events
          </button>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          {error}
          <button
            onClick={() => setError('')}
            style={styles.errorClose}
          >
            ×
          </button>
        </div>
      )}

      <div style={styles.toolbar}>
        <div style={styles.toolbarLeft}>
          {canDownloadRegistrations && (
            <button
              type="button"
              className="my-events-toolbar-icon-btn"
              style={styles.toolbarIconButton}
              onClick={handleOpenBulkDownload}
              title="Download event data or registrations"
              aria-label="Download event data or registrations CSV"
              aria-haspopup="dialog"
            >
              <DownloadIcon />
            </button>
          )}
          {canDownloadDocuments && (
            <button
              type="button"
              className="my-events-toolbar-icon-btn"
              style={styles.toolbarIconButton}
              onClick={handleOpenDocuments}
              title="Open documents library"
              aria-label="Open documents library for fliers and verification forms"
              aria-haspopup="dialog"
            >
              <FolderIcon />
            </button>
          )}
        </div>
        <div style={styles.toolbarRight}>
        <button
          type="button"
          className="my-events-help-bubble"
          style={styles.helpBubble}
          onClick={() => {
            setFilterMenuOpen(false);
            setRegistrationsPopupEvent(null);
            setBulkDownloadOpen(false);
            setHelpOpen(prev => !prev);
          }}
          aria-expanded={helpOpen}
          aria-haspopup="dialog"
        >
          <span style={styles.helpBubbleIcon} aria-hidden>?</span>
          <span>Help</span>
        </button>
        <div style={styles.filterDropdownWrap} ref={filterMenuRef}>
          <button
            type="button"
            className="my-events-filter-badge"
            style={{
              ...styles.filterDropdownBadge,
              ...(appliedFilter ? styles.filterDropdownBadgeActive : {}),
            }}
            onClick={() => {
              setHelpOpen(false);
              setBulkDownloadOpen(false);
              setFilterMenuOpen(prev => !prev);
            }}
            aria-expanded={filterMenuOpen}
            aria-haspopup="listbox"
          >
            <span>{activeFilterLabel}</span>
            <span style={styles.filterDropdownChevron} aria-hidden>▾</span>
          </button>
          {filterMenuOpen && (
            <div style={styles.filterDropdownMenu} role="listbox" aria-label="Filter events">
              {filterOptions.map(option => (
                <button
                  key={option.value || 'no-filter'}
                  type="button"
                  role="option"
                  aria-selected={appliedFilter === option.value}
                  style={{
                    ...styles.filterDropdownOption,
                    ...(appliedFilter === option.value ? styles.filterDropdownOptionActive : {}),
                  }}
                  onClick={() => handleFilterSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {canDownloadRegistrations && (
          <button
            type="button"
            className="my-events-present-btn"
            style={styles.presentButton}
            onClick={handleOpenPresent}
            aria-haspopup="dialog"
            title="Open presentable analytics"
          >
            <span style={styles.presentButtonIcon} aria-hidden>◧</span>
            <span>Present</span>
          </button>
        )}
        </div>
      </div>

      {helpOpen && (
        <div className="my-events-help-layer" role="presentation">
          <button
            type="button"
            className="my-events-help-backdrop"
            aria-label="Close help"
            onClick={() => setHelpOpen(false)}
          />
          <div
            className="my-events-help-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-events-help-title"
          >
            <div style={styles.helpPopupHeader}>
              <h2 id="my-events-help-title" style={styles.helpPopupTitle}>Help</h2>
              <button
                type="button"
                className="my-events-help-close"
                style={styles.helpPopupClose}
                onClick={() => setHelpOpen(false)}
                aria-label="Close help"
              >
                ×
              </button>
            </div>
            <div style={styles.helpPopupBody}>
              {renderHelpLegendContent()}
            </div>
          </div>
        </div>
      )}

      {renderRegistrationsPopup()}

      {renderBulkDownloadPopup()}

      {renderPresentOverlay()}

      {renderDocumentsOverlay()}

      {getFilteredEvents().length === 0 ? (
        <div style={styles.emptyState}>
          <p>{appliedFilter ? 'No events match the selected filter.' : 'No events found.'}</p>
        </div>
      ) : (
        <div style={styles.tableWrap} ref={listTopRef}>
          <table className="my-events-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Type</th>
                <th>Stage</th>
                <th>Status</th>
                <th style={styles.actionsCol} aria-label="Actions" />
              </tr>
            </thead>
              {getDisplayEvents().map((event) => {
                const isRowOpen = openRows[event.id];
                const showInvitations = isRowOpen && invitationsOpen[event.id] && hasInvitationsPanel(event);

                return (
                <tbody
                  key={event.id}
                  className={['my-events-group', isRowOpen && 'my-events-group--expanded'].filter(Boolean).join(' ')}
                >
                  <tr
                    ref={el => { eventRefs.current[event.id] = el; }}
                    className={[
                      'my-events-row',
                      isRowOpen && 'my-events-row--expanded',
                      (isEventFiltered(event.id) || lastActionEventId === event.id) && 'my-events-row--highlight',
                    ].filter(Boolean).join(' ')}
                  >
                    <td
                      style={styles.cellTitle}
                      title={getEventTitle(event)}
                      onClick={() => handleRowClick(event.id, event)}
                    >
                      {getEventTitle(event)}
                    </td>
                    <td
                      style={styles.cellNowrap}
                      onClick={() => handleRowClick(event.id, event)}
                    >
                      {dateTimeToMonDDYYYYWithTime(event.date, false)}
                    </td>
                    <td
                      style={styles.cellType}
                      onClick={() => handleRowClick(event.id, event)}
                    >
                      {event.type || '—'}
                    </td>
                    <td onClick={() => handleRowClick(event.id, event)}>
                      <span style={{ ...styles.pillBadge, ...getStageBadgeStyle(event.stage) }}>
                        {getStageDisplayLabel(event.stage)}
                      </span>
                    </td>
                    <td onClick={() => handleRowClick(event.id, event)}>
                      <span style={{ ...styles.pillBadge, ...getStatusBadgeStyle(event.status, event.stage) }}>
                        {event.status}
                      </span>
                    </td>
                    <td
                      style={styles.cellActions}
                      onClick={() => handleRowClick(event.id, event)}
                    >
                      <div style={styles.rowActions}>
                        {canShowSafIcon(event) && (
                          <a
                            href={getSafFormHref(event.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="my-events-icon-btn"
                            title={getSafEditTitle(event)}
                            aria-label={getSafEditTitle(event)}
                            onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <OpenInNewTabIcon />
                          </a>
                        )}
                        {getAcceptedTrainerInvitations(event).length > 0 && (
                          <div
                            role="button"
                            tabIndex={0}
                            className="my-events-icon-btn"
                            title={hasMultipleAcceptedTrainerInvitations(event) ? 'Choose training plan' : 'Training Plan'}
                            aria-label={hasMultipleAcceptedTrainerInvitations(event)
                              ? 'Open invitations to choose a training plan'
                              : `Open training plan for ${getAcceptedTrainerInvitations(event)[0].concentration_title}`}
                            onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                            onKeyDown={(e) => handleIconButtonKeyDown(e, (ev) => handleHammerClick(event.id, event, ev))}
                            onClick={(e) => handleHammerClick(event.id, event, e)}
                          >
                            <HammerIcon />
                          </div>
                        )}
                        {hasInvitationsPanel(event) && (() => {
                          const pendingInvitationCount = getPendingInvitationActionCount(event);
                          return (
                          <div
                            role="button"
                            tabIndex={0}
                            className={[
                              'my-events-icon-btn',
                              'my-events-icon-btn--invitations',
                              invitationsOpen[event.id] && 'my-events-icon-btn--active',
                            ].filter(Boolean).join(' ')}
                            title={getInvitationsIconLabel(event)}
                            aria-label={getInvitationsIconLabel(event)}
                            onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                            onKeyDown={(e) => handleIconButtonKeyDown(e, (ev) => toggleInvitations(event.id, ev))}
                            onClick={(e) => toggleInvitations(event.id, e)}
                          >
                            <EnvelopeIcon />
                            {pendingInvitationCount > 0 && (
                              <span className="my-events-icon-btn-count">{pendingInvitationCount}</span>
                            )}
                          </div>
                          );
                        })()}
                        {canViewRegistrations && (
                          <div
                            role="button"
                            tabIndex={0}
                            className={[
                              'my-events-icon-btn',
                              registrationsPopupEvent?.id === event.id && 'my-events-icon-btn--active',
                            ].filter(Boolean).join(' ')}
                            title="Registrations"
                            aria-label={`View registrations for ${event.title}`}
                            onMouseDown={(e) => e.button === 0 && e.preventDefault()}
                            onKeyDown={(e) => handleIconButtonKeyDown(e, (ev) => handleOpenRegistrations(event, ev))}
                            onClick={(e) => handleOpenRegistrations(event, e)}
                          >
                            <ListIcon />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isRowOpen && (
                    <tr className="my-events-detail-row">
                      <td colSpan={6}>
                        {event.description && (
                          <p
                            style={{
                              ...styles.detailDescription,
                              ...(fullDetails[event.id] ? styles.detailDescriptionFull : styles.detailDescriptionLimited),
                              ...(isDescriptionTruncated(event.description) ? styles.detailDescriptionClickable : {}),
                            }}
                            onClick={isDescriptionTruncated(event.description)
                              ? (e) => toggleFullDetails(event.id, e)
                              : undefined}
                          >
                            {fullDetails[event.id] ? event.description : truncateText(event.description, 100)}
                          </p>
                        )}
                        {event.coordinatorInvitation && (
                          <p style={{
                            ...styles.detailLead,
                            ...(fullDetails[event.id] ? {} : styles.detailLeadLimited),
                          }}>
                            <span style={styles.metaLabel}>Lead instructor:</span>{' '}
                            {fullDetails[event.id]
                              ? `${event.first_name} ${event.last_name}${event.primary_email ? ` · ${event.primary_email}` : ''}`
                              : truncateText(`${event.first_name} ${event.last_name}${event.primary_email ? ` · ${event.primary_email}` : ''}`, 80)
                            }
                          </p>
                        )}
                        {!event.description && !event.coordinatorInvitation && (
                          <span style={styles.detailEmpty}>No additional details.</span>
                        )}
                      </td>
                    </tr>
                  )}
                  {showInvitations && (
                    <tr className="my-events-invitations-row">
                      <td colSpan={6}>{renderInvitationsPanel(event)}</td>
                    </tr>
                  )}
                </tbody>
                );
              })}
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '1rem 1.25rem',
    minHeight: '100vh',
  },
  title: {
    fontSize: '1.2rem',
    color: '#1e293b',
    margin: '0 0 8px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '28px',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  toolbarIconButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    border: '1px solid #e2e8f0',
    borderRadius: '999px',
    backgroundColor: '#fff',
    color: '#475569',
    cursor: 'pointer',
    lineHeight: 1,
  },
  helpBubble: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 11px',
    border: '1px solid #e2e8f0',
    borderRadius: '999px',
    backgroundColor: '#fff',
    color: '#475569',
    fontSize: '0.72rem',
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: 1.2,
  },
  helpBubbleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#eff6ff',
    color: '#1d4e89',
    fontSize: '0.62rem',
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
  },
  helpPopupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 14px 10px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  helpPopupTitle: {
    margin: 0,
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1e293b',
    letterSpacing: '-0.02em',
  },
  helpPopupClose: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    color: '#64748b',
    fontSize: '1.25rem',
    lineHeight: 1,
    cursor: 'pointer',
  },
  helpPopupBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 14px 16px',
    overflowY: 'auto',
  },
  registrationsPopupCount: {
    fontWeight: 500,
    color: '#94a3b8',
    fontSize: '0.82em',
  },
  registrationsPopupEventMeta: {
    padding: '0 14px 8px',
    fontSize: '0.68rem',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    lineHeight: 1.4,
  },
  registrationsPopupError: {
    padding: '8px 10px',
    borderRadius: '6px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    fontSize: '0.72rem',
  },
  registrationsPopupLoading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0',
  },
  registrationsPopupEmpty: {
    margin: 0,
    fontSize: '0.72rem',
    color: '#94a3b8',
    textAlign: 'center',
    padding: '12px 0',
  },
  registrationsNoLink: {
    color: '#cbd5e1',
  },
  registrationsPopupHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  registrationsDownloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '999px',
    backgroundColor: '#fff',
    color: '#475569',
    fontSize: '0.66rem',
    fontWeight: 600,
    cursor: 'pointer',
    lineHeight: 1.2,
  },
  bulkDownloadIntro: {
    margin: 0,
    fontSize: '0.7rem',
    color: '#64748b',
    lineHeight: 1.4,
  },
  bulkDownloadTab: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: '0.72rem',
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: 1.2,
  },
  bulkDownloadTabActive: {
    backgroundColor: '#eff6ff',
    borderColor: '#bfdbfe',
    color: '#1d4e89',
    fontWeight: 600,
  },
  bulkDownloadDateRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  bulkDownloadDateField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 140px',
  },
  bulkDownloadDateLabel: {
    fontSize: '0.62rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#94a3b8',
  },
  bulkDownloadDateInput: {
    padding: '6px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.72rem',
    color: '#334155',
    backgroundColor: '#fff',
  },
  bulkDownloadActionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  bulkDownloadSelectionMeta: {
    fontSize: '0.68rem',
    color: '#64748b',
  },
  bulkDownloadActionButtons: {
    display: 'flex',
    gap: '8px',
  },
  bulkDownloadLinkButton: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#1d4e89',
    fontSize: '0.68rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  bulkDownloadEventItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '6px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fafbfc',
    cursor: 'pointer',
  },
  bulkDownloadEventText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  bulkDownloadEventTitle: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#334155',
    lineHeight: 1.3,
  },
  bulkDownloadEventMeta: {
    fontSize: '0.64rem',
    color: '#94a3b8',
    lineHeight: 1.3,
  },
  bulkDownloadFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '4px',
    borderTop: '1px solid #e2e8f0',
  },
  bulkDownloadCancelButton: {
    padding: '6px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: '0.72rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  bulkDownloadConfirmButton: {
    padding: '6px 12px',
    border: '1px solid #1d4e89',
    borderRadius: '6px',
    backgroundColor: '#1d4e89',
    color: '#fff',
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  legendGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  legendGroupTitle: {
    fontSize: '0.62rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#94a3b8',
    marginBottom: '1px',
  },
  roleLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  roleLegendText: {
    fontSize: '0.7rem',
    color: '#64748b',
    lineHeight: 1.4,
  },
  pillBadge: {
    padding: '2px 7px',
    borderRadius: '999px',
    fontSize: '0.58rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    flexShrink: 0,
    lineHeight: 1.3,
  },
  topBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 10px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    marginBottom: '8px',
    color: '#475569',
    fontSize: '0.7rem',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterDropdownWrap: {
    position: 'relative',
  },
  presentButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    border: '1px solid #1d4e89',
    borderRadius: '999px',
    backgroundColor: '#1d4e89',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    lineHeight: 1.2,
  },
  presentButtonIcon: {
    fontSize: '0.8rem',
    lineHeight: 1,
  },
  filterDropdownBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '0.68rem',
    fontWeight: 500,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    lineHeight: 1.35,
  },
  filterDropdownBadgeActive: {
    backgroundColor: '#334155',
    borderColor: '#334155',
    color: '#fff',
  },
  filterDropdownChevron: {
    fontSize: '0.6rem',
    lineHeight: 1,
    opacity: 0.85,
  },
  filterDropdownMenu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    right: 0,
    minWidth: '220px',
    padding: '4px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    zIndex: 20,
  },
  filterDropdownOption: {
    display: 'block',
    width: '100%',
    padding: '6px 10px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: '#475569',
    fontSize: '0.72rem',
    fontWeight: 500,
    textAlign: 'left',
    cursor: 'pointer',
    lineHeight: 1.35,
  },
  filterDropdownOptionActive: {
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
  },
  error: {
    padding: '0.45rem 0.6rem',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    border: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.78rem',
  },
  errorClose: {
    background: 'none',
    border: 'none',
    fontSize: '1rem',
    cursor: 'pointer',
    color: '#94a3b8',
    lineHeight: 1,
  },
  emptyState: {
    textAlign: 'center',
    padding: '1.25rem',
    color: '#64748b',
    fontSize: '0.82rem',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  actionsCol: {
    width: '140px',
  },
  cellTitle: {
    fontWeight: 600,
    color: '#1e293b',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellNowrap: {
    whiteSpace: 'nowrap',
    color: '#64748b',
  },
  cellType: {
    color: '#64748b',
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellActions: {
    width: '140px',
    textAlign: 'right',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  iconButtonLegend: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '26px',
    height: '26px',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    background: '#fff',
    color: '#64748b',
    flexShrink: 0,
  },
  detailDescription: {
    margin: '0 0 4px',
    fontSize: '0.72rem',
    color: '#475569',
    lineHeight: 1.45,
  },
  detailDescriptionLimited: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailDescriptionClickable: {
    cursor: 'pointer',
  },
  detailDescriptionFull: {
    whiteSpace: 'pre-wrap',
  },
  detailLead: {
    margin: 0,
    fontSize: '0.72rem',
    color: '#64748b',
    lineHeight: 1.4,
  },
  detailLeadLimited: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailEmpty: {
    fontSize: '0.72rem',
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  invitationsPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 0',
  },
  metaLabel: {
    color: '#94a3b8',
    fontWeight: 600,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  actionPrompt: {
    fontSize: '0.72rem',
    color: '#475569',
    lineHeight: 1.4,
    minWidth: 0,
  },
  actionStatus: {
    color: '#94a3b8',
    fontWeight: 500,
  },
  actionSubtext: {
    display: 'block',
    marginTop: '3px',
    fontSize: '0.65rem',
    color: '#94a3b8',
    lineHeight: 1.4,
    fontWeight: 400,
  },
  inlineActionIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    margin: '0 1px',
    color: '#64748b',
  },
  noticeRow: {
    padding: '5px 0',
  },
  buttonGroup: {
    display: 'flex',
    gap: '5px',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  button: {
    padding: '0.22rem 0.55rem',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    fontSize: '0.7rem',
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    backgroundColor: '#fff',
    color: '#475569',
  },
  acceptButton: {},
  declineButton: {},
  approveButton: {},
  denyButton: {},
  forceApproveButton: {},
  disabledButton: {
    backgroundColor: '#f8fafc',
    color: '#94a3b8',
    borderColor: '#e2e8f0',
    cursor: 'not-allowed',
  },
  decisionHistory: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px solid #f1f5f9',
    fontSize: '0.72rem',
    color: '#475569',
  },
  sectionHeading: {
    margin: '0 0 6px',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#475569',
  },
  messageText: {
    margin: 0,
    fontSize: '0.72rem',
    lineHeight: 1.4,
  },
  historyEntry: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
    borderBottom: '1px solid #f1f5f9',
    gap: '8px',
  },
  historyInfo: {
    flex: 1,
    minWidth: 0,
  },
  historyAction: {
    fontWeight: 600,
    color: '#334155',
    textTransform: 'capitalize',
    fontSize: '0.72rem',
  },
  historyStage: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    marginLeft: '4px',
  },
  historyCoordinator: {
    fontSize: '0.68rem',
    color: '#64748b',
    fontWeight: 500,
    marginLeft: '4px',
  },
  historyTimestamp: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    flexShrink: 0,
  },
  safButton: {},
  clearFilterButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#fff',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    fontSize: '0.68rem',
    cursor: 'pointer',
    fontWeight: 500,
    flexShrink: 0,
  },
};

export default MyEvents; 